import React, { useState, useEffect, useMemo } from 'react';
import { db, auth } from '../firebase';
import { ref, onValue, update, remove, get, push, set, serverTimestamp } from 'firebase/database';
import { Plus, Trash2, Edit3, MessageSquare, Eye, User, QrCode, TrendingUp, Home as HomeIcon, X, AlertCircle, Calendar, CreditCard, PlusSquare, ChevronRight, ShoppingBag, Copy, Printer, Share2, CheckCircle2, Search, Clock, Database, Camera, Star } from 'lucide-react';
import Chat from './Chat';
import AddRoomModal from './AddRoomModal';
import EditPropertyModal from './EditPropertyModal';
import AddActivityModal from './AddActivityModal';
import BookingModal from './BookingModal';
import QrScanner from './QrScanner';
import HistoricalDataImport from './HistoricalDataImport';
import { format, parse, addDays, isBefore, isAfter, differenceInDays } from 'date-fns';
import { encryptText } from '../utils/encryption';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import TouristProfileModal from './TouristProfileModal';
import { sendBookingStatusUpdateEmail } from '../services/emailService';
import { parseDateSafely, formatBookingDateRange } from '../utils/dateUtils';
import { ReviewPhotoViewer, CompactReviewCard } from './ReviewPhotoViewer';
export { parseDateSafely, formatBookingDateRange };

const ChatRoomItem = ({ room, onClick }) => {
  const [photo, setPhoto] = useState(room.otherProfilePic || null);

  useEffect(() => {
    if (room.otherProfilePic) return;

    const fetchPhoto = async () => {
      try {
        const propSnap = await get(ref(db, `properties/${room.otherUid}`));
        if (propSnap.exists()) {
          const data = propSnap.val();
          const imgs = Array.isArray(data.imageUrls) ? data.imageUrls : (data.imageUrls ? Object.values(data.imageUrls) : []);
          if (imgs.length > 0) setPhoto(imgs[0]);
        } else {
          const userSnap = await get(ref(db, `users/${room.otherUid}`));
          if (userSnap.exists() && userSnap.val().profilePicUrl) {
            setPhoto(userSnap.val().profilePicUrl);
          }
        }
      } catch (e) {
        console.error("Chat photo fetch error", e);
      }
    };
    fetchPhoto();
  }, [room.otherUid, room.otherProfilePic]);

  return (
    <div
      className="card chat-room-card"
      style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '16px', padding: '16px', transition: 'var(--transition)' }}
      onClick={() => onClick(room)}
    >
      <div style={{
        width: '52px', height: '52px', borderRadius: '18px',
        background: 'var(--light-bg)', overflow: 'hidden',
        display: 'flex', justifyContent: 'center', alignItems: 'center', color: 'var(--text-muted)'
      }}>
        {photo ? (
          <img src={photo} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
        ) : (
          <User size={28} />
        )}
      </div>
      <div style={{ flex: 1 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <h4 style={{ margin: 0, fontWeight: 800 }}>{room.otherUserName}</h4>
          <span style={{ fontSize: '10px', color: 'var(--text-muted)' }}>{room.timestamp ? format(new Date(room.timestamp), 'p') : ''}</span>
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '4px' }}>
          <p style={{ margin: 0, fontSize: '13px', color: 'var(--text-muted)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
            View messages
          </p>
          {room.unreadCount > 0 && (
            <span style={{ background: 'var(--primary)', color: 'white', fontSize: '11px', fontWeight: 800, padding: '2px 8px', borderRadius: '12px' }}>
              {room.unreadCount}
            </span>
          )}
        </div>
      </div>
    </div>
  );
};

const OwnerDashboard = ({ profile, uid }) => {
  const [activeTab, setActiveTab] = useState('Rooms');
  const [rooms, setRooms] = useState([]);
  const [activities, setActivities] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [readReviews, setReadReviews] = useState(() => {
    try {
      const stored = localStorage.getItem(`read_reviews_${uid}`);
      return stored ? JSON.parse(stored) : {};
    } catch (e) {
      return {};
    }
  });
  const [replyingReviewId, setReplyingReviewId] = useState(null);
  const [replyText, setReplyText] = useState('');
  const [submittingReply, setSubmittingReply] = useState(false);
  const [viewerPhotos, setViewerPhotos] = useState(null);
  const [viewerIndex, setViewerIndex] = useState(0);
  const [showAddActivity, setShowAddActivity] = useState(false);
  const [activityToEdit, setActivityToEdit] = useState(null);
  const [bookings, setBookings] = useState([]);
  const [chatRooms, setChatRooms] = useState([]);
  const [selectedChat, setSelectedChat] = useState(null);
  const [showAddRoom, setShowAddRoom] = useState(false);
  const [showEditProperty, setShowEditProperty] = useState(false);
  const [showScanner, setShowScanner] = useState(false);
  const [showBreakdownBooking, setShowBreakdownBooking] = useState(null);
  const [previewRoom, setPreviewRoom] = useState(null);
  const [breakdownAddonPrices, setBreakdownAddonPrices] = useState({});
  const [showRevenue, setShowRevenue] = useState(false);
  const [roomToEdit, setRoomToEdit] = useState(null);
  const [roomToDisable, setRoomToDisable] = useState(null);
  const [disableStartDate, setDisableStartDate] = useState(null);
  const [disableEndDate, setDisableEndDate] = useState(null);
  const [blockedDates, setBlockedDates] = useState([]);
  const [roomToDelete, setRoomToDelete] = useState(null);
  const [scannedBooking, setScannedBooking] = useState(null);
  const [scannedViaQr, setScannedViaQr] = useState(false);
  const [showPayoutModal, setShowPayoutModal] = useState(false);
  const [foodMenuUrls, setFoodMenuUrls] = useState([]);
  const [isUploadingMenu, setIsUploadingMenu] = useState(false);
  const [selectedTouristUid, setSelectedTouristUid] = useState(null);
  const [scannedTouristPhoto, setScannedTouristPhoto] = useState(null);
  const [scannedTouristName, setScannedTouristName] = useState('');
  const [scannedTouristGcashName, setScannedTouristGcashName] = useState(null);
  const [scannedTouristGcashNumber, setScannedTouristGcashNumber] = useState(null);
  const [revenueFilter, setRevenueFilter] = useState('All');
  const [revenueYearFilter, setRevenueYearFilter] = useState('All');
  const [expandedMonth, setExpandedMonth] = useState(null);
  const [bookingLimit, setBookingLimit] = useState(10);
  const [roomLimit, setRoomLimit] = useState(8);
  const [bookingFilter, setBookingFilter] = useState('All');
  const [balanceSearchQuery, setBalanceSearchQuery] = useState('');
  const [selectedBalances, setSelectedBalances] = useState({});
  const [confirmAction, setConfirmAction] = useState({
    isOpen: false,
    bookingId: null,
    newStatus: '',
    requireReason: false,
    reason: '',
    message: '',
    isDestructive: false,
    hasBalance: false,
    balanceAmount: 0,
    markAsPaid: false
  });
  const [extendStayConfig, setExtendStayConfig] = useState({ isOpen: false, bookingId: null, nights: 1, isLoading: false, error: '' });
  const [confirmPaymentAction, setConfirmPaymentAction] = useState({ isOpen: false, type: '', payload: null, message: '' });
  const [activitySchedule, setActivitySchedule] = useState('');
  const [isEditingSchedule, setIsEditingSchedule] = useState(false);
  const [savingSchedule, setSavingSchedule] = useState(false);
  const [previewReceipt, setPreviewReceipt] = useState(null); // URL or string for in-system floating receipt view
  const [showActivities, setShowActivities] = useState(true);
  const [enableCustomMenuUpload, setEnableCustomMenuUpload] = useState(false);
  const [updatingShowActivities, setUpdatingShowActivities] = useState(false);

  const markReviewAsRead = (reviewId) => {
    if (!reviewId || readReviews[reviewId]) return;
    setReadReviews(prev => {
      const next = { ...prev, [reviewId]: true };
      try {
        localStorage.setItem(`read_reviews_${uid}`, JSON.stringify(next));
      } catch (e) {}
      return next;
    });
  };

  const markAllReviewsAsRead = () => {
    const next = { ...readReviews };
    reviews.forEach(r => {
      if (r.id) next[r.id] = true;
    });
    setReadReviews(next);
    try {
      localStorage.setItem(`read_reviews_${uid}`, JSON.stringify(next));
    } catch (e) {}
  };

  const toggleShowActivities = async () => {
    setUpdatingShowActivities(true);
    try {
      const nextVal = !showActivities;
      await update(ref(db, `properties/${uid}`), {
        showActivities: nextVal
      });
      setShowActivities(nextVal);
    } catch (err) {
      alert('Failed to update activity visibility: ' + err.message);
    } finally {
      setUpdatingShowActivities(false);
    }
  };

  const seedStandardActivities = async () => {
    if (!window.confirm("Add the standard activities catalog (Kayak ₱300, Paddle Board ₱300, Boatride to falls ₱1450, Boatride with meal ₱2000, Karaoke ₱750)? Existing activities won't be replaced.")) return;
    try {
      const standardList = [
        { title: 'Kayak', price: 300, maxPax: 1, description: 'Single kayak rental for river and resort exploration.', timeSlots: ['07:00 AM', '10:00 AM', '01:00 PM', '03:00 PM'], imageUrls: ['https://images.unsplash.com/photo-1544551763-46a013bb70d5?auto=format&fit=crop&w=600&q=80'] },
        { title: 'Paddle Board', price: 300, maxPax: 1, description: 'Stand-up paddle boarding experience on calm waters.', timeSlots: ['07:00 AM', '10:00 AM', '01:00 PM', '03:00 PM'], imageUrls: ['https://images.unsplash.com/photo-1507525428034-b723cf961d3e?auto=format&fit=crop&w=600&q=80'] },
        { title: 'Boatride to falls', price: 1450, maxPax: 3, description: 'Scenic guided boat tour to the famous falls. Minimum 1 pax, maximum 3 pax. (₱750 surcharge applies if solo passenger).', timeSlots: ['08:00 AM', '11:00 AM', '01:30 PM'], imageUrls: ['https://images.unsplash.com/photo-1506744038136-46273834b3fb?auto=format&fit=crop&w=600&q=80'] },
        { title: 'Boatride to falls with meal', price: 2000, maxPax: 3, description: 'Guided boat ride to the falls with complete set meal. Minimum 1 pax, maximum 3 pax. (₱750 surcharge applies if solo passenger).', timeSlots: ['08:00 AM', '11:00 AM', '01:30 PM'], imageUrls: ['https://images.unsplash.com/photo-1540555700478-4be289fbecef?auto=format&fit=crop&w=600&q=80'] },
        { title: 'Karaoke', price: 750, maxPax: 15, description: 'Full sound system and microphone karaoke session for your group.', timeSlots: ['10:00 AM', '02:00 PM', '06:00 PM'], imageUrls: ['https://images.unsplash.com/photo-1516450360452-9312f5e86fc7?auto=format&fit=crop&w=600&q=80'] }
      ];

      for (const act of standardList) {
        const actRef = push(ref(db, `properties/${uid}/activities`));
        await set(actRef, { ...act, timestamp: Date.now() });
      }
      alert('Standard activities successfully added!');
    } catch (e) {
      alert('Failed to load standard activities: ' + e.message);
    }
  };

  const saveActivitySchedule = async () => {
    setSavingSchedule(true);
    try {
      await update(ref(db, `properties/${uid}`), {
        activitySchedule: activitySchedule
      });
      setIsEditingSchedule(false);
      alert('Activity schedule updated successfully!');
    } catch (e) {
      console.error('Error saving activity schedule:', e);
      alert('Failed to update activity schedule: ' + e.message);
    } finally {
      setSavingSchedule(false);
    }
  };

  const getBalance = (b) => {
    const total = parseFloat(b.totalPrice) || 0;
    const paid = parseFloat(b.amountPaid || (b.paymentOption?.includes('30%') ? total * 0.3 : total)) || 0;
    return total - paid;
  };

  const initiatePaymentAction = (type, payload) => {
    let message = '';
    if (type === 'single') {
      message = 'Are you sure you want to mark this booking as paid?';
    } else if (type === 'selected') {
      message = 'Are you sure you want to mark the selected bookings as paid?';
    } else if (type === 'all') {
      message = 'Are you sure you want to mark all unpaid bookings for this guest as paid?';
    }
    setConfirmPaymentAction({ isOpen: true, type, payload, message });
  };

  const handleConfirmPayment = async () => {
    const { type, payload } = confirmPaymentAction;
    if (type === 'single') {
      await markAsPaid(payload.bookingId, payload.totalPrice);
    } else if (type === 'selected') {
      await markSelectedAsPaid(payload);
    } else if (type === 'all') {
      await markAllAsPaid(payload);
    }
    setConfirmPaymentAction({ isOpen: false, type: '', payload: null, message: '' });
  };

  const markAsPaid = async (bookingId, total) => {
    try {
      await update(ref(db, `bookings/${bookingId}`), {
        amountPaid: total,
        paymentStatus: 'paid'
      });
    } catch (e) {
      console.error('Error marking as paid:', e);
    }
  };

  const markAllAsPaid = async (touristGroup) => {
    try {
      const updates = {};
      touristGroup.bookings.forEach(b => {
        updates[`bookings/${b.id}/amountPaid`] = parseFloat(b.totalPrice) || 0;
        updates[`bookings/${b.id}/paymentStatus`] = 'paid';
      });
      await update(ref(db), updates);
      
      setSelectedBalances(prev => {
        const next = { ...prev };
        touristGroup.bookings.forEach(b => delete next[b.id]);
        return next;
      });
    } catch (e) {
      console.error('Error marking all as paid:', e);
    }
  };

  const markSelectedAsPaid = async (touristGroup) => {
    try {
      const updates = {};
      touristGroup.bookings.forEach(b => {
        if (selectedBalances[b.id]) {
          updates[`bookings/${b.id}/amountPaid`] = parseFloat(b.totalPrice) || 0;
          updates[`bookings/${b.id}/paymentStatus`] = 'paid';
        }
      });
      if (Object.keys(updates).length > 0) {
        await update(ref(db), updates);
        
        setSelectedBalances(prev => {
          const next = { ...prev };
          touristGroup.bookings.forEach(b => {
            if (next[b.id]) delete next[b.id];
          });
          return next;
        });
      }
    } catch (e) {
      console.error('Error marking selected as paid:', e);
    }
  };

  const toggleBalanceSelection = (bookingId) => {
    setSelectedBalances(prev => ({ ...prev, [bookingId]: !prev[bookingId] }));
  };

  const balancesByTourist = useMemo(() => {
    const grouped = {};
    bookings.forEach(b => {
      const bal = getBalance(b);
      if (bal > 0 && ['Pending', 'Confirmed', 'Checked In'].includes(b.status || 'Pending')) {
        const tUid = b.touristUid || 'unknown';
        if (!grouped[tUid]) {
          grouped[tUid] = { touristName: b.touristName || 'Unknown', touristUid: tUid, bookings: [], totalBalance: 0 };
        }
        grouped[tUid].bookings.push(b);
        grouped[tUid].totalBalance += bal;
      }
    });
    
    const q = balanceSearchQuery.toLowerCase();
    return Object.values(grouped).filter(g => 
      g.touristName.toLowerCase().includes(q) || 
      g.bookings.some(b => 
        b.id.toLowerCase().includes(q) || 
        (b.bookingDate && b.bookingDate.toLowerCase().includes(q)) ||
        (b.checkInDate && b.checkInDate.toLowerCase().includes(q)) ||
        (b.date && b.date.toLowerCase().includes(q))
      )
    );
  }, [bookings, balanceSearchQuery]);

  const handleCopyReport = (month) => {
    if (!stats.monthlyRevenue[month]) return;
    let reportText = `${month} Sales Report\nTotal Revenue: ₱${stats.monthlyRevenue[month]}\n\nBookings:\n`;
    const details = stats.monthDetails[month] || [];
    details.forEach(b => {
      reportText += `- ${b.room}: ₱${b.amount} (${b.tourist})\n`;
    });

    navigator.clipboard.writeText(reportText).then(() => {
      alert("Report copied to clipboard!");
    }).catch(err => {
      console.error("Failed to copy report", err);
      alert("Failed to copy report.");
    });
  };

  useEffect(() => {
    if (showBreakdownBooking && !showBreakdownBooking.pricing && showBreakdownBooking.selectedAddons?.length > 0) {
      const propId = showBreakdownBooking.propertyId || showBreakdownBooking.ownerUid || uid;
      if (propId) {
        get(ref(db, `properties/${propId}/addonPrices`)).then(snap => {
          if (snap.exists()) {
            setBreakdownAddonPrices(snap.val());
          } else {
            setBreakdownAddonPrices({});
          }
        }).catch(() => setBreakdownAddonPrices({}));
      }
    } else {
      setBreakdownAddonPrices({});
    }
  }, [showBreakdownBooking, uid]);

  useEffect(() => {
    if (!scannedBooking || !scannedBooking.touristUid) return;

    const fetchTouristData = async () => {
      try {
        const userSnap = await get(ref(db, `users/${scannedBooking.touristUid}`));
        if (userSnap.exists()) {
          const val = userSnap.val();
          if (val.profilePicUrl) {
            setScannedTouristPhoto(val.profilePicUrl);
          } else {
            setScannedTouristPhoto(null);
          }
          const tName = val.firstName || val.name || val.fullName;
          const tLast = val.lastName ? ` ${val.lastName}` : '';
          if (tName) setScannedTouristName(`${tName}${tLast}`.trim());
          else setScannedTouristName(scannedBooking.touristName || 'Guest');
          setScannedTouristGcashName(val.gcashName && val.gcashName.trim() ? val.gcashName : 'N/A');
          setScannedTouristGcashNumber(val.gcashNumber && val.gcashNumber.trim() ? val.gcashNumber : 'N/A');
        }
      } catch (e) {
        console.error("Tourist data fetch error", e);
      }
    };
    fetchTouristData();
  }, [scannedBooking]);

  useEffect(() => {
    if (!uid) return;

    const roomsRef = ref(db, `properties/${uid}/roomInventory`);
    const unsubscribeRooms = onValue(roomsRef, (snapshot) => {
      const data = snapshot.val();
      const list = data ? Object.entries(data)
        .map(([id, val]) => ({ id, ...val }))
        .sort((a, b) => {
          const titleA = a.title || '';
          const titleB = b.title || '';
          const titleCompare = titleA.localeCompare(titleB);
          if (titleCompare !== 0) return titleCompare;
          return (b.timestamp || 0) - (a.timestamp || 0);
        }) : [];
      setRooms(list);
    });

    const activitiesRef = ref(db, `properties/${uid}/activities`);
    const unsubscribeActivities = onValue(activitiesRef, (snapshot) => {
      const data = snapshot.val();
      const list = data ? Object.entries(data).map(([id, val]) => ({ id, ...val })) : [];
      setActivities(list);
    });

    const propertyRef = ref(db, `properties/${uid}`);
    const unsubscribeProperty = onValue(propertyRef, (snapshot) => {
      if (snapshot.exists()) {
        const val = snapshot.val();
        if (val.activitySchedule !== undefined) {
          setActivitySchedule(val.activitySchedule || '');
        }
        if (val.showActivities !== undefined) {
          setShowActivities(val.showActivities === true || val.showActivities === 'true');
        } else if (val.type === 'Hotel') {
          setShowActivities(false);
        } else {
          setShowActivities(true);
        }
        if (val.enableCustomMenuUpload !== undefined) {
          setEnableCustomMenuUpload(val.enableCustomMenuUpload === true || val.enableCustomMenuUpload === 'true');
        } else {
          setEnableCustomMenuUpload(false);
        }
        if (val.foodMenuUrls) {
          setFoodMenuUrls(val.foodMenuUrls || []);
        } else {
          setFoodMenuUrls([]);
        }
      }
    });

    const bookingsRef = ref(db, 'bookings');
    const unsubscribeBookings = onValue(bookingsRef, (snapshot) => {
      const data = snapshot.val();
      const list = data ? Object.entries(data)
        .map(([id, val]) => ({ id, ...val }))
        .filter(b => b.ownerUid === uid)
        .sort((a, b) => {
          const aTime = (typeof a.timestamp === 'number') ? a.timestamp : (a.timestamp && typeof a.timestamp === 'object' ? Date.now() : 0);
          const bTime = (typeof b.timestamp === 'number') ? b.timestamp : (b.timestamp && typeof b.timestamp === 'object' ? Date.now() : 0);
          return bTime - aTime;
        }) : [];

      // Automatically transition un-checked-in expired bookings to 'No Show'
      const today = new Date();
      today.setHours(0, 0, 0, 0);

      list.forEach(async (b) => {
        const rawStatus = (b.status || '').trim().toLowerCase();
        if (rawStatus === 'confirmed' || rawStatus === 'pending') {
          const dateStr = b.bookingDate || b.checkInDate || b.date;
          if (dateStr) {
            try {
              const bDate = parseDateSafely(dateStr);
              if (bDate) {
                bDate.setHours(0, 0, 0, 0);
                const nights = parseInt(b.nights) || 1;
                const endDate = addDays(bDate, nights);
                endDate.setHours(0, 0, 0, 0);

                if (today > endDate) {
                  // Mark as No Show in Firebase
                  await update(ref(db, `bookings/${b.id}`), {
                    status: 'No Show',
                    cancellationReason: 'Guest did not check in and checkout date has passed.'
                  });

                  // Send notification to tourist if available
                  if (b.touristUid) {
                    const itemTitle = b.activityTitle || b.roomTitle || 'Reservation';
                    await push(ref(db, `notifications/${b.touristUid}`), {
                      title: 'Booking Marked as No Show',
                      message: `Your reservation for "${itemTitle}" was automatically marked as No Show because the checkout date has passed without check-in.`,
                      type: 'booking_updated',
                      isRead: false,
                      timestamp: serverTimestamp(),
                      bookingId: b.id
                    });
                  }
                }
              }
            } catch (err) {
              console.error('Error auto-marking no show:', err);
            }
          }
        }
      });

      setBookings(list);
    });

    const chatRoomsRef = ref(db, `chat_rooms/${uid}`);
    const unsubscribeChats = onValue(chatRoomsRef, (snapshot) => {
      const data = snapshot.val();
      const list = data ? Object.entries(data).map(([otherUid, val]) => ({
        otherUid,
        ...val
      })).sort((a, b) => {
        const aTime = a.timestamp;
        const bTime = b.timestamp;
        const aNum = (typeof aTime === 'number') ? aTime : (aTime && typeof aTime === 'object' ? Date.now() : 0);
        const bNum = (typeof bTime === 'number') ? bTime : (bTime && typeof bTime === 'object' ? Date.now() : 0);
        return bNum - aNum;
      }) : [];
      setChatRooms(list);
    });

    const reviewsRef = ref(db, `reviews/${uid}`);
    const unsubscribeReviews = onValue(reviewsRef, (snapshot) => {
      const data = snapshot.val();
      const list = data ? Object.entries(data).map(([id, val]) => ({
        id,
        ...val
      })).sort((a, b) => (b.timestamp || 0) - (a.timestamp || 0)) : [];
      setReviews(list);
    });

    return () => {
      unsubscribeRooms();
      unsubscribeActivities();
      unsubscribeProperty();
      unsubscribeBookings();
      unsubscribeChats();
      unsubscribeReviews();
    };
  }, [uid]);

  useEffect(() => {
    const handleOpenChat = (e) => {
      if (e.detail?.otherUid) {
        setSelectedChat({
          otherUid: e.detail.otherUid,
          otherUserName: e.detail.otherUserName || 'User'
        });
      }
    };
    window.addEventListener('rc-open-chat', handleOpenChat);
    return () => window.removeEventListener('rc-open-chat', handleOpenChat);
  }, []);

  const stats = useMemo(() => {
    let totalRevenue = 0;
    let totalPending = 0;
    const monthlyRevenue = {};
    const monthlyPending = {};
    const roomSales = {};
    const availableMonths = ['All'];
    const availableYears = ['All'];
    const monthDetails = {};
    const pendingDetails = {};

    bookings.forEach(b => {
      const status = (b.status || '').toLowerCase();

      try {
        const dateStr = b.bookingDate || b.checkInDate || b.date;
        const date = parseDateSafely(dateStr);
        if (date) {
          const monthKey = format(date, 'MMMM yyyy');
          const yearKey = format(date, 'yyyy');
          if (!availableMonths.includes(monthKey)) availableMonths.push(monthKey);
          if (!availableYears.includes(yearKey)) availableYears.push(yearKey);

          if (!['declined', 'refund approved', 'refund confirmed', 'refund requested'].includes(status)) {
            // If historical record has countsTowardRevenue === false, do not count toward revenue
            if (b.isHistorical === true && b.countsTowardRevenue === false) {
              return;
            }

            const total = parseFloat(b.totalPrice || b.amount || 0);
            let paid = parseFloat(b.amountPaid) || 0;
            const payOption = (b.paymentOption || b.paymentMethod || '').toString().toLowerCase();

            if (paid === 0 && total > 0) {
              if (payOption.includes('30%') || payOption.includes('downpayment')) {
                paid = total * 0.3;
              } else if (payOption.includes('full') || payOption.includes('100%') || b.paymentStatus === 'paid') {
                paid = total;
              }
            }

            if (['completed', 'checked out'].includes(status) && paid === 0) {
              paid = total;
            }

            let pending = 0;
            if (status !== 'cancelled') {
              pending = Math.max(0, total - paid);
            }

            if (pending > 0) {
              if (!pendingDetails[monthKey]) pendingDetails[monthKey] = [];
              pendingDetails[monthKey].push({
                room: b.activityTitle || b.roomTitle || b.room || b.roomId || 'Unknown Room',
                date: dateStr,
                parsedDate: date.getTime(),
                nights: b.nights || 1,
                tourist: b.touristName || b.customerName || b.userName || b.name || b.fullName || 'Guest',
                amount: pending,
                rawBooking: b
              });
            }

            if (paid > 0) {
              if (!monthDetails[monthKey]) monthDetails[monthKey] = [];

              monthDetails[monthKey].push({
                room: b.activityTitle || b.roomTitle || b.room || b.roomId || 'Unknown Room',
                date: dateStr,
                parsedDate: date.getTime(),
                nights: b.nights || 1,
                tourist: b.touristName || b.customerName || b.userName || b.name || b.fullName || 'Guest',
                amount: paid,
                rawBooking: b
              });
            }

            const matchYear = revenueYearFilter === 'All' || yearKey === revenueYearFilter;
            const matchMonth = revenueFilter === 'All' || monthKey === revenueFilter;

            if (matchYear && matchMonth) {
              if (paid > 0) {
                totalRevenue += paid;
                monthlyRevenue[monthKey] = (monthlyRevenue[monthKey] || 0) + paid;
                const room = b.activityTitle || b.roomTitle || 'Unknown Room';
                roomSales[room] = (roomSales[room] || 0) + 1;
              }
              if (pending > 0) {
                totalPending += pending;
                monthlyPending[monthKey] = (monthlyPending[monthKey] || 0) + pending;
              }
            }
          }
        }
      } catch (err) {
        console.error("Error parsing booking for revenue", err);
      }
    });

    Object.keys(monthDetails).forEach(key => {
      monthDetails[key].sort((a, b) => a.parsedDate - b.parsedDate);
    });
    Object.keys(pendingDetails).forEach(key => {
      pendingDetails[key].sort((a, b) => a.parsedDate - b.parsedDate);
    });

    // Sort availableMonths and monthlyRevenue chronologically (Jan to Dec)
    const sortedMonthlyRevenue = {};
    Object.keys(monthlyRevenue)
      .sort((a, b) => {
        const da = parseDateSafely(a);
        const db = parseDateSafely(b);
        return (da ? da.getTime() : 0) - (db ? db.getTime() : 0);
      })
      .forEach(k => {
        sortedMonthlyRevenue[k] = monthlyRevenue[k];
      });

    const bestSeller = Object.keys(roomSales).length > 0
      ? Object.entries(roomSales).reduce((a, b) => a[1] > b[1] ? a : b)[0]
      : "No sales yet";

    return { totalRevenue, totalPending, monthlyRevenue: sortedMonthlyRevenue, monthlyPending, bestSeller, roomCount: rooms.length, bookingCount: bookings.length, availableMonths, availableYears, monthDetails, pendingDetails };
  }, [bookings, rooms.length, revenueFilter, revenueYearFilter]);

  // Helper to extract normalized activity items from a booking
  const getBookingActivityItems = (b) => {
    if (!b) return [];
    const items = [];
    if (Array.isArray(b.activityList) && b.activityList.length > 0) {
      b.activityList.forEach(act => {
        items.push({
          id: act.id || act.activityId || '',
          title: act.title || act.name || '',
          timeSlot: act.timeSlot || act.arrivalTime || b.timeSlot || b.arrivalTime || ''
        });
      });
    } else if (Array.isArray(b.selectedActivities) && b.selectedActivities.length > 0) {
      b.selectedActivities.forEach(act => {
        items.push({
          id: act.id || act.activityId || '',
          title: act.title || act.name || '',
          timeSlot: act.timeSlot || act.arrivalTime || b.timeSlot || b.arrivalTime || ''
        });
      });
    } else if (b.activityId || b.activityTitle || b.isActivityBooking) {
      items.push({
        id: b.activityId || '',
        title: b.activityTitle || 'Activity',
        timeSlot: b.timeSlot || b.arrivalTime || ''
      });
    }
    return items;
  };

  const isKaraokeName = (str) => {
    return (str || '').toLowerCase().includes('karaoke');
  };

  // Helper to check if two bookings conflict (room overlap, karaoke same day, or activity same day & timeslot)
  const bookingsConflict = (bA, bB) => {
    if (!bA || !bB || bA.id === bB.id) return false;

    // Check room conflict
    const roomA = bA.roomId || (!bA.isActivityBooking && !bA.activityId ? bA.roomTitle : null);
    const roomB = bB.roomId || (!bB.isActivityBooking && !bB.activityId ? bB.roomTitle : null);
    if (roomA && roomB && roomA === roomB) {
      const startA = parseDateSafely(bA.bookingDate || bA.checkInDate || bA.date);
      const startB = parseDateSafely(bB.bookingDate || bB.checkInDate || bB.date);
      if (startA && startB) {
        const endA = addDays(startA, parseInt(bA.nights) || 1);
        const endB = addDays(startB, parseInt(bB.nights) || 1);
        if (isBefore(startA, endB) && isAfter(endA, startB)) {
          return { hasConflict: true, type: 'room', title: bB.roomTitle || bA.roomTitle || 'Room' };
        }
      }
    }

    // Check activity conflicts
    const actsA = getBookingActivityItems(bA);
    const actsB = getBookingActivityItems(bB);

    if (actsA.length > 0 && actsB.length > 0) {
      const startA = parseDateSafely(bA.bookingDate || bA.checkInDate || bA.date);
      const startB = parseDateSafely(bB.bookingDate || bB.checkInDate || bB.date);

      if (startA && startB && format(startA, 'yyyy-MM-dd') === format(startB, 'yyyy-MM-dd')) {
        // 1. Karaoke is whole-day exclusive
        const hasKaraokeA = actsA.some(a => isKaraokeName(a.title));
        const hasKaraokeB = actsB.some(b => isKaraokeName(b.title));
        if (hasKaraokeA && hasKaraokeB) {
          return { hasConflict: true, type: 'activity', title: 'Karaoke' };
        }

        // 2. Hourly activities check same activity and same time slot
        for (const itemA of actsA) {
          if (isKaraokeName(itemA.title)) continue;
          const titleA = (itemA.title || '').trim().toLowerCase();
          const idA = String(itemA.id || '').trim().toLowerCase();
          const slotA = (itemA.timeSlot || '').trim().toLowerCase();

          for (const itemB of actsB) {
            if (isKaraokeName(itemB.title)) continue;
            const titleB = (itemB.title || '').trim().toLowerCase();
            const idB = String(itemB.id || '').trim().toLowerCase();
            const slotB = (itemB.timeSlot || '').trim().toLowerCase();

            const isSameActivity = (idA && idB && idA === idB) ||
              (titleA && titleB && titleA === titleB) ||
              (titleA.includes('boatride') && titleB.includes('boatride')) ||
              (titleA.includes('kayak') && titleB.includes('kayak')) ||
              (titleA.includes('paddle') && titleB.includes('paddle'));

            if (isSameActivity) {
              // If both have specific time slots, conflict occurs if they match
              if (slotA && slotB && slotA !== 'regular operating hours' && slotB !== 'regular operating hours') {
                if (slotA === slotB) {
                  return { hasConflict: true, type: 'activity', title: itemB.title || itemA.title };
                }
              } else {
                // If either has no slot specified or full-day booking on the same date
                return { hasConflict: true, type: 'activity', title: itemB.title || itemA.title };
              }
            }
          }
        }
      }
    }

    return false;
  };

  const checkConflict = (targetBooking, allBookings) => {
    try {
      return allBookings.some(b => {
        if (b.id === targetBooking.id) return false;
        const status = (b.status || '').toLowerCase();
        if (status !== 'confirmed' && status !== 'checked in') return false;
        return !!bookingsConflict(targetBooking, b);
      });
    } catch (e) {
      console.error("Conflict check error:", e);
      return false;
    }
  };

  const updateUserBalance = async (touristUid) => {
    if (!touristUid) return;
    try {
      const snap = await get(ref(db, 'bookings'));
      if (snap.exists()) {
        let balance = 0;
        const allBookings = snap.val();
        Object.values(allBookings).forEach(b => {
          if (b.touristUid === touristUid) {
            const status = (b.status || '').toLowerCase();
            if (status !== 'cancelled' && status !== 'declined' && status !== 'refund approved' && status !== 'refund confirmed') {
              const grandTotal = b.pricing?.grandTotal || b.totalPrice || 0;
              const paid = b.amountPaid || 0;
              balance += (grandTotal - paid);
            }
          }
        });
        balance = Math.max(0, balance);
        await update(ref(db, `users/${touristUid}`), { totalOutstandingBalance: balance });
      }
    } catch (e) {
      console.error("Failed to update user balance", e);
    }
  };

  const updateStatus = async (bookingId, newStatus, providedReason = null) => {
    try {
      let cancellationReason = providedReason;

      if (newStatus === 'Reschedule Approved') {
        const target = bookings.find(b => b.id === bookingId);
        if (target && target.requestedRescheduleDate) {
          const isAct = target.isActivityBooking === true || !!target.activityId || (target.activityTitle && !target.roomId);
          const newDur = target.requestedRescheduleHours || target.requestedRescheduleNights || target.hours || target.nights || 1;
          const updatePayload = {
            status: 'Confirmed',
            bookingDate: target.requestedRescheduleDate,
            nights: newDur,
            requestedRescheduleDate: null,
            requestedRescheduleNights: null,
            requestedRescheduleHours: null
          };
          if (isAct) {
            updatePayload.hours = newDur;
          }
          await update(ref(db, `bookings/${bookingId}`), updatePayload);
          newStatus = 'Confirmed';
        }
      } else if (newStatus === 'Reschedule Declined') {
        const updates = {
          status: 'Confirmed',
          requestedRescheduleDate: null,
          requestedRescheduleNights: null,
          requestedRescheduleHours: null
        };
        if (providedReason) updates.cancellationReason = providedReason;
        await update(ref(db, `bookings/${bookingId}`), updates);
        newStatus = 'Reschedule Request Declined';
      } else if (newStatus === 'Refund Declined') {
        const updates = {
          status: 'Confirmed',
          refundReason: null
        };
        if (providedReason) updates.cancellationReason = providedReason;
        await update(ref(db, `bookings/${bookingId}`), updates);
        newStatus = 'Refund Request Declined';
      } else {

        if (newStatus === 'Confirmed') {
          const target = bookings.find(b => b.id === bookingId);
          if (target && checkConflict(target, bookings)) {
            alert("Cannot confirm: This booking overlaps with an existing confirmed reservation for the same unit or activity schedule.");
            return;
          }

          if (target) {
            // Auto-decline any other pending bookings that booked the exact same room or activity slot
            for (const b of bookings) {
              if (b.id !== bookingId && (b.status || '').toLowerCase() === 'pending') {
                const conflict = bookingsConflict(target, b);
                if (conflict) {
                  const isActB = conflict.type === 'activity';
                  const itemTitle = conflict.title || b.activityTitle || b.roomTitle || (isActB ? 'Activity' : 'Room');
                  const declineReason = `This ${isActB ? 'activity slot' : 'room'} was booked by another guest. Please request a refund or reschedule your booking.`;

                  await update(ref(db, `bookings/${b.id}`), {
                    status: 'Declined',
                    cancellationReason: declineReason
                  });

                  if (b.touristUid) {
                    await push(ref(db, `notifications/${b.touristUid}`), {
                      title: 'Booking Declined',
                      message: `Your booking for "${itemTitle}" was declined because this slot was reserved by another guest. Please request a refund or reschedule your booking.`,
                      type: 'booking_rejected',
                      isRead: false,
                      timestamp: serverTimestamp(),
                      bookingId: b.id
                    });
                  }
                }
              }
            }
          }
        }

        const bookingRef = ref(db, `bookings/${bookingId}`);
        const updates = { status: newStatus };
        if (newStatus === 'Refund Confirmed') {
          updates.refundConfirmedAt = serverTimestamp();
          updates.refundConfirmedBy = auth.currentUser?.uid || null;
        }
        if (cancellationReason) {
          updates.cancellationReason = cancellationReason;
        }

        const targetForUpdate = bookings.find(b => b.id === bookingId);
        if (targetForUpdate && (newStatus === 'Confirmed' || newStatus === 'Checked In' || newStatus === 'Completed')) {
          const grandTotal = targetForUpdate.pricing?.grandTotal || targetForUpdate.totalPrice || targetForUpdate.total || targetForUpdate.amount || targetForUpdate.payment || targetForUpdate.price || 0;
          
          if (providedReason === true) { // Using providedReason parameter to pass markAsPaid for 'Checked In'
            updates.amountPaid = grandTotal;
          }
          
          const paid = (providedReason === true) ? grandTotal : (targetForUpdate.amountPaid || 0);
          if (paid >= grandTotal) {
            updates.paymentStatus = 'fully_paid';
          } else if (paid > 0) {
            updates.paymentStatus = 'partially_paid';
          } else {
            updates.paymentStatus = 'unpaid';
          }
        }

        await update(bookingRef, updates);
        if (targetForUpdate && targetForUpdate.touristUid) {
          await updateUserBalance(targetForUpdate.touristUid);
        }
      }

      const target = bookings.find(b => b.id === bookingId);
      if (target && target.touristUid) {
        let notifType = 'booking_updated';
        if (newStatus === 'Confirmed') notifType = 'booking_accepted';
        else if (newStatus === 'Cancelled' || newStatus.includes('Declined')) notifType = 'booking_rejected';
        else if (newStatus === 'Completed' || newStatus === 'Refund Confirmed') notifType = 'booking_completed';

        let notifTitle = 'Booking Updated';
        let message = `Your booking for "${target.activityTitle || target.roomTitle || 'Room'}" is now ${newStatus}.`;
        let sysMessage = `System: Your booking for "${target.activityTitle || target.roomTitle || 'Room'}" is now ${newStatus}.`;

        const roomName = target.activityTitle || target.roomTitle || 'Room';
        if (newStatus === 'Confirmed' || newStatus === 'Approved') {
          message = `Good day! We are pleased to inform you that your booking for "${roomName}" has been officially Approved. We look forward to hosting you!`;
          sysMessage = `System: Good day! We are pleased to inform you that your booking for "${roomName}" has been officially Approved. We look forward to hosting you!`;
        } else if (newStatus === 'Refund Approved') {
          notifTitle = 'Refund Approved - Please Wait';
          message = `Your refund request for "${roomName}" has been APPROVED. Please wait for a moment while the owner transfers the refund to your GCash account.`;
          sysMessage = `System: Your refund request for "${roomName}" has been APPROVED. Please wait for a moment while the owner transfers the refund to your GCash account.`;
        } else if (newStatus === 'Refund Confirmed') {
          notifTitle = 'Refund Confirmed';
          message = `Your refund for "${roomName}" has been confirmed by the resort.`;
          sysMessage = `System: Your refund for "${roomName}" has been confirmed by the resort.`;
        } else if (newStatus === 'Checked In') {
          message = `Welcome to the resort! Your check-in for "${roomName}" is now complete. We hope you have a wonderful stay with us.`;
          sysMessage = `System: Welcome to the resort! Your check-in for "${roomName}" is now complete. We hope you have a wonderful stay with us.`;
        } else if (newStatus === 'Checked Out' || newStatus === 'Completed') {
          message = `Thank you for staying with us in "${roomName}". Your check-out is complete. We hope to see you again soon!`;
          sysMessage = `System: Thank you for staying with us in "${roomName}". Your check-out is complete. We hope to see you again soon!`;
        }

        if (cancellationReason) {
          message += ` Reason: ${cancellationReason}`;
          sysMessage += ` Reason: ${cancellationReason}`;
        }

        await push(ref(db, `notifications/${target.touristUid}`), {
          title: notifTitle,
          message: message,
          type: notifType,
          isRead: false,
          timestamp: serverTimestamp(),
        });

        // EmailJS Booking Status Update Trigger
        try {
          const touristSnap = await get(ref(db, `users/${target.touristUid}`));
          const touristData = touristSnap.exists() ? touristSnap.val() : {};
          const touristEmail = touristData.email || target.touristEmail;
          if (touristEmail) {
            sendBookingStatusUpdateEmail({
              toEmail: touristEmail,
              toName: touristData.firstName ? `${touristData.firstName} ${touristData.lastName || ''}`.trim() : (target.touristName || 'Guest'),
              bookingId: target.id,
              propertyName: target.propertyName || profile?.propertyName || profile?.name || 'Resort',
              roomName: roomName,
              newStatus: newStatus,
              notes: cancellationReason || undefined
            }).catch(e => console.warn('[EmailJS] Status email error:', e));
          }
        } catch (e) {
          console.warn('[EmailJS] Failed to fetch tourist for email:', e);
        }

        const tUid = target.touristUid;
        if (uid && tUid) {
          const ids = [uid, tUid].sort();
          const chatId = ids.join('_');
          const encryptedMessage = encryptText(sysMessage, chatId);

          await push(ref(db, `chats/${chatId}/messages`), {
            senderUid: uid,
            text: encryptedMessage,
            timestamp: serverTimestamp(),
            seen: false
          });

          await update(ref(db, `chat_rooms/${uid}/${tUid}`), {
            lastMessage: encryptedMessage,
            timestamp: serverTimestamp()
          });

          const otherChatRoomRef = ref(db, `chat_rooms/${tUid}/${uid}`);
          const otherSnap = await get(otherChatRoomRef);
          const currentUnread = otherSnap.exists() && otherSnap.val().unreadCount ? otherSnap.val().unreadCount : 0;
          await update(otherChatRoomRef, {
            lastMessage: encryptedMessage,
            timestamp: serverTimestamp(),
            unreadCount: currentUnread + 1
          });
        }
      }
    } catch (err) {
      alert("Status update failed: " + err.message);
    }
  };

  const initiateUpdateStatus = (bookingId, newStatus) => {
    let msg = '';
    let reqReason = false;

    if (newStatus === 'Cancelled') {
      msg = 'Please provide a reason for declining/cancelling this booking:';
      reqReason = true;
    } else if (newStatus === 'Reschedule Declined') {
      msg = 'Please provide a reason for declining this reschedule request:';
      reqReason = true;
    } else if (newStatus === 'Refund Declined') {
      msg = 'Please provide a reason for declining this refund:';
      reqReason = true;
    } else if (newStatus === 'Reschedule Approved') {
      const target = bookings.find(b => b.id === bookingId);
      const isAct = target?.isActivityBooking === true || !!target?.activityId || (target?.activityTitle && !target?.roomId);
      const dur = target?.requestedRescheduleHours || target?.requestedRescheduleNights || target?.hours || target?.nights || 1;
      const unit = isAct ? 'hour/s' : 'nights';
      msg = `Approve reschedule to ${target?.requestedRescheduleDate} (${dur} ${unit})?`;
    } else if (newStatus === 'Refund Approved') {
      msg = `Are you sure you want to approve this refund?`;
    } else if (newStatus === 'Refund Confirmed') {
      msg = `Confirm that the refund has been sent to the guest?`;
    } else if (newStatus === 'Completed') {
      msg = `Are you sure you want to complete the check-out for this guest?`;
    } else {
      msg = `Are you sure you want to mark this booking as ${newStatus}?`;
    }

    let hasBalance = false;
    let balanceAmount = 0;
    if (newStatus === 'Checked In') {
      const target = bookings.find(b => b.id === bookingId);
      if (target) {
        const total = parseFloat(target.pricing?.grandTotal || target.totalPrice || target.total || target.amount || target.payment || target.price || 0);
        const paid = parseFloat(target.amountPaid || 0);
        if (total - paid > 0) {
          hasBalance = true;
          balanceAmount = total - paid;
        }
      }
    }

    setConfirmAction({
      isOpen: true,
      bookingId,
      newStatus,
      requireReason: reqReason,
      reason: '',
      message: msg,
      isDestructive: ['Cancelled', 'Reschedule Declined', 'Refund Declined'].includes(newStatus),
      hasBalance,
      balanceAmount,
      markAsPaid: false
    });
  };

  const handleExtendStay = async () => {
    if (!extendStayConfig.bookingId) return;
    setExtendStayConfig(prev => ({ ...prev, isLoading: true, error: '' }));
    try {
      const b = bookings.find(bk => bk.id === extendStayConfig.bookingId);
      if (!b) throw new Error('Booking not found');

      const currentNights = parseInt(b.nights) || 1;
      const newNights = currentNights + extendStayConfig.nights;
      const tempBooking = { ...b, nights: newNights };

      if (checkConflict(tempBooking, bookings)) {
        setExtendStayConfig(prev => ({ ...prev, isLoading: false, error: 'Conflict: Room is already booked during the extended dates.' }));
        return;
      }

      let grandTotal = parseFloat(b.pricing?.grandTotal || b.totalPrice || 0);
      let oldBasePrice = parseFloat(b.pricing?.basePrice || 0);
      
      if (!b.pricing?.basePrice) {
        let addonsTotal = 0;
        if (Array.isArray(b.selectedAddons)) {
          addonsTotal = b.selectedAddons.reduce((sum, addon) => sum + parseFloat(addon.price || 0), 0);
        }
        oldBasePrice = Math.max(0, grandTotal - addonsTotal);
      }

      const pricePerNight = currentNights > 0 ? (oldBasePrice / currentNights) : 0;
      const extraCost = pricePerNight * extendStayConfig.nights;

      const updates = { nights: newNights };
      if (b.pricing) {
        updates.pricing = {
          ...b.pricing,
          basePrice: oldBasePrice + extraCost,
          grandTotal: grandTotal + extraCost
        };
      } else {
        updates.totalPrice = grandTotal + extraCost;
      }

      await update(ref(db, `bookings/${b.id}`), updates);
      
      setExtendStayConfig({ isOpen: false, bookingId: null, nights: 1, isLoading: false, error: '' });
      alert(`Stay extended successfully! Added \u20b1${extraCost.toFixed(2)} to balance.`);
    } catch (e) {
      console.error(e);
      setExtendStayConfig(prev => ({ ...prev, isLoading: false, error: 'An error occurred: ' + e.message }));
    }
  };

  const handleConfirmActionSubmit = async () => {
    if (confirmAction.requireReason && !confirmAction.reason.trim()) {
      alert("Reason is required.");
      return;
    }
    const { bookingId, newStatus, reason, markAsPaid, hasBalance } = confirmAction;
    if (hasBalance && newStatus === 'Checked In' && !markAsPaid) {
      return;
    }
    
    setConfirmAction({ isOpen: false, bookingId: null, newStatus: null, requireReason: false, reason: '', message: '', isDestructive: false, hasBalance: false, balanceAmount: 0, markAsPaid: false });
    if (scannedBooking) {
      setScannedBooking(null);
    }
    
    const passedReasonOrPaid = (newStatus === 'Checked In') ? markAsPaid : reason;
    await updateStatus(bookingId, newStatus, passedReasonOrPaid);
  };

  const deleteBooking = async (id) => {
    const target = bookings.find(b => b.id === id);
    await remove(ref(db, `bookings/${id}`));
    if (target && target.touristUid) {
      await updateUserBalance(target.touristUid);
    }
  };

  const handleDisableRoomClick = (room) => {
    if (room.isDisabled) {
      update(ref(db, `properties/${uid}/roomInventory/${room.id}`), {
        isDisabled: false,
        disabledStartDate: null,
        disabledDays: null
      });
      return;
    }

    const activeBookingsForRoom = bookings.filter(b => 
      (b.roomId === room.id || b.activityId === room.id) &&
      ['Pending', 'Confirmed', 'Reschedule Requested', 'Checked In'].includes(b.status || 'Pending')
    );

    const checkedInBookings = activeBookingsForRoom.filter(b => b.status === 'Checked In');

    if (checkedInBookings.length > 0) {
      alert("WARNING: There is currently a tourist checked into this room. You cannot disable it until they check out.");
      return;
    }

    let dates = [];
    for (const b of activeBookingsForRoom) {
      const dateStr = b.bookingDate || b.checkInDate || b.date;
      if (dateStr) {
        try {
          const bookingStart = parseDateSafely(dateStr);
          if (bookingStart) {
            const nights = parseInt(b.nights) || 1;
            for (let i = 0; i < nights; i++) {
              const d = addDays(bookingStart, i);
              dates.push(new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime());
            }
          }
        } catch(e) {}
      }
    }
    setBlockedDates(dates);

    setRoomToDisable(room);
    setDisableStartDate(null);
    setDisableEndDate(null);
  };

  const confirmDisableRoom = async () => {
    if (!disableStartDate || !disableEndDate) {
      alert("Please select both a start date and an end date.");
      return;
    }

    if (disableEndDate < disableStartDate) {
      alert("End date must be after start date.");
      return;
    }

    const disableStart = new Date(disableStartDate.getFullYear(), disableStartDate.getMonth(), disableStartDate.getDate());
    const disableEnd = new Date(disableEndDate.getFullYear(), disableEndDate.getMonth(), disableEndDate.getDate());
    
    const days = differenceInDays(disableEnd, disableStart) + 1;

    let conflict = false;
    for (let i = 0; i < days; i++) {
      const current = addDays(disableStart, i).getTime();
      if (blockedDates.includes(current)) {
        conflict = true;
        break;
      }
    }

    if (conflict) {
      alert("WARNING: The selected disable dates overlap with an existing booking. Please select different dates.");
      return;
    }

    await update(ref(db, `properties/${uid}/roomInventory/${roomToDisable.id}`), {
      isDisabled: true,
      disabledStartDate: format(disableStart, 'yyyy-MM-dd'),
      disabledDays: days
    });
    setRoomToDisable(null);
  };

  const deleteRoom = async (id) => {
    // Check if there are active bookings
    const activeBookings = bookings.filter(b => 
      (b.roomId === id || b.activityId === id) &&
      ['pending', 'confirmed', 'checked in', 'checked-in'].includes((b.status || '').toLowerCase())
    );

    if (activeBookings.length > 0) {
      let warningMessage = "Cannot delete room because it has active bookings on the following dates:\n\n";
      let latestCheckoutDate = null;

      activeBookings.forEach(b => {
        const dateStr = b.bookingDate || b.checkInDate || b.date;
        if (dateStr) {
          try {
            const start = parseDateSafely(dateStr);
            if (start) {
              const nights = parseInt(b.nights) || 1;
              const end = addDays(start, nights);
              
              warningMessage += `- ${format(start, 'MMM dd, yyyy')} to ${format(end, 'MMM dd, yyyy')}\n`;

              if (!latestCheckoutDate || end > latestCheckoutDate) {
                latestCheckoutDate = end;
              }
            } else {
              warningMessage += `- ${dateStr}\n`;
            }
          } catch (e) {
            warningMessage += `- ${dateStr}\n`;
          }
        }
      });
      
      if (latestCheckoutDate) {
        warningMessage += `\nYou will have the ability to delete this room after the supposed checkout on ${format(latestCheckoutDate, 'MMM dd, yyyy')}.`;
      }

      alert(warningMessage);
      setRoomToDelete(null);
      return;
    }

    try {
      await remove(ref(db, `properties/${uid}/roomInventory/${id}`));
      setRoomToDelete(null);
    } catch (e) {
      console.error(e);
      alert("Error deleting room: " + e.message);
    }
  };

  if (selectedChat) {
    return (
      <Chat
        currentUid={uid}
        otherUserUid={selectedChat.otherUid}
        otherUserName={selectedChat.otherUserName}
        onBack={() => setSelectedChat(null)}
      />
    );
  }

  return (
    <div className="owner-dashboard" style={{ width: '100%' }}>
      <div className="owner-top-nav-bar" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '28px', flexWrap: 'wrap', gap: '12px' }}>
        <div className="tab-container owner-tab-container" style={{
          display: 'flex', flexWrap: 'wrap', gap: '6px', background: 'var(--nav-group-bg)',
          padding: '6px', borderRadius: '20px', maxWidth: '100%',
          alignItems: 'center'
        }}>
          {['Rooms', 'Activities', 'Food Menu', 'Bookings', 'Balances', 'Reviews', 'Chat', 'Import Data'].map(tab => {
            const isChat = tab === 'Chat';
            const isBookings = tab === 'Bookings';
            const isReviews = tab === 'Reviews';
            const totalUnread = isChat ? chatRooms.reduce((sum, room) => sum + (parseInt(room.unreadCount) || 0), 0) : 0;
            const pendingBookings = isBookings ? bookings.filter(b => b.status === 'Pending').length : 0;
            const unreadReviews = isReviews ? reviews.filter(r => !readReviews[r.id]).length : 0;
            const badgeCount = isChat ? totalUnread : (isBookings ? pendingBookings : (isReviews ? unreadReviews : 0));

            return (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                className={`owner-tab-btn ${activeTab === tab ? 'active' : ''}`}
                style={{
                  background: activeTab === tab ? 'var(--surface)' : 'transparent',
                  border: 'none',
                  color: activeTab === tab ? 'var(--primary)' : 'var(--text-muted)',
                  boxShadow: activeTab === tab ? '0 4px 12px rgba(0,0,0,0.08)' : 'none',
                  transition: 'var(--transition)',
                  display: 'flex',
                  alignItems: 'center',
                  whiteSpace: 'nowrap'
                }}
              >
                {tab}
                {badgeCount > 0 && (
                  <span
                    className="owner-tab-badge"
                    style={{
                      background: isBookings ? '#EF4444' : 'var(--primary)'
                    }}
                  >
                    {badgeCount}
                  </span>
                )}
              </button>
            )
          })}
        </div>
        <div className="owner-scanner-wrapper" style={{ display: 'flex', gap: '12px' }}>
          <button
            className="btn btn-primary"
            style={{
              padding: '12px',
              borderRadius: '16px',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              border: 'none',
              outline: 'none',
              boxShadow: 'none'
            }}
            onClick={() => { setShowScanner(true); }}
            title="Scan Booking QR"
          >
            <QrCode size={22} />
          </button>
        </div>
      </div>

      {activeTab === 'Rooms' && (
        <section className="view-transition">
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '14px', marginBottom: '24px' }}>
            <div
              className="glass-stat-card"
              style={{ justifyContent: 'center' }}
              onClick={() => document.getElementById('room-inventory')?.scrollIntoView({ behavior: 'smooth' })}
              title="Click to view Room Inventory"
            >
              <div
                className="glass-stat-icon"
                style={{
                  background: 'var(--secondary-soft)',
                  border: '1px solid rgba(29, 211, 176, 0.25)',
                  boxShadow: '0 4px 12px rgba(29, 211, 176, 0.12)'
                }}
              >
                <HomeIcon color="var(--secondary)" size={20} />
              </div>
              <div style={{ minWidth: 0 }}>
                <div style={{ fontSize: '22px', fontWeight: 900, lineHeight: 1.15, letterSpacing: '-0.5px' }}>
                  {stats.roomCount}
                </div>
                <div style={{ fontSize: '11px', color: 'var(--text-muted)', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.6px', marginTop: '2px' }}>
                  Rooms
                </div>
              </div>
            </div>

            <div
              className="glass-stat-card"
              style={{ justifyContent: 'center' }}
              onClick={() => setActiveTab('Bookings')}
              title="Click to view Bookings"
            >
              <div
                className="glass-stat-icon"
                style={{
                  background: 'var(--primary-soft)',
                  border: '1px solid rgba(251, 54, 64, 0.25)',
                  boxShadow: '0 4px 12px rgba(251, 54, 64, 0.12)'
                }}
              >
                <Calendar color="var(--primary)" size={20} />
              </div>
              <div style={{ minWidth: 0 }}>
                <div style={{ fontSize: '22px', fontWeight: 900, lineHeight: 1.15, letterSpacing: '-0.5px' }}>
                  {stats.bookingCount}
                </div>
                <div style={{ fontSize: '11px', color: 'var(--text-muted)', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.6px', marginTop: '2px' }}>
                  Bookings
                </div>
              </div>
            </div>

            <div
              className="glass-stat-card"
              style={{ justifyContent: 'center' }}
              onClick={() => setShowRevenue(true)}
              title="Click to view revenue breakdown"
            >
              <div
                className="glass-stat-icon"
                style={{
                  background: 'rgba(29, 211, 176, 0.12)',
                  border: '1px solid rgba(29, 211, 176, 0.25)',
                  boxShadow: '0 4px 12px rgba(29, 211, 176, 0.12)'
                }}
              >
                <TrendingUp color="var(--secondary)" size={20} />
              </div>
              <div style={{ minWidth: 0 }}>
                <div style={{ fontSize: '20px', fontWeight: 900, lineHeight: 1.15, letterSpacing: '-0.5px', color: 'var(--secondary)' }}>
                  ₱{stats.totalRevenue.toLocaleString()}
                </div>
                <div style={{ fontSize: '11px', color: 'var(--text-muted)', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.6px', marginTop: '2px' }}>
                  Earnings · tap to view
                </div>
              </div>
            </div>
          </div>

          <div id="room-inventory" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <h3 style={{ margin: 0, fontSize: '22px', fontWeight: 800 }}>Room Inventory</h3>
              <button
                className="btn"
                style={{ background: 'var(--light-bg)', color: 'var(--text-muted)', padding: '8px', borderRadius: '10px', border: '1px solid var(--border)' }}
                onClick={() => setShowEditProperty(true)}
              >
                <Edit3 size={16} />
              </button>
            </div>
            <button
              className="btn btn-secondary"
              onClick={() => { setRoomToEdit(null); setShowAddRoom(true); }}
              style={{ borderRadius: '14px', padding: '10px 20px', cursor: 'pointer' }}
            >
              <Plus size={18} /> Add New Room
            </button>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: '24px' }}>
            {rooms.length > 0 ? (
              <>
                {rooms.slice(0, roomLimit).map(room => {
                  const isConfirmingDelete = roomToDelete === room.id;
                  const imgSrc = (Array.isArray(room.imageUrls) ? room.imageUrls[0] : Object.values(room.imageUrls || {})[0]) || 'https://via.placeholder.com/400x200?text=No+Photo';
                  return (
                    <div key={room.id} className="room-card">
                      <div style={{ position: 'relative', height: '190px', overflow: 'hidden', cursor: 'pointer' }} onClick={() => setPreviewRoom(room)} title="Click to preview how tourists see this room">
                        <img src={imgSrc} alt={room.title} className="room-card-img" />
                        <div style={{
                          position: 'absolute', inset: 0,
                          background: 'linear-gradient(to top, rgba(0,0,0,0.55) 0%, transparent 55%)'
                        }} />
                        <div style={{
                          position: 'absolute', top: '12px', right: '12px',
                          background: 'rgba(255,255,255,0.95)', backdropFilter: 'blur(8px)',
                          padding: '5px 12px', borderRadius: '10px',
                          fontWeight: 900, color: 'var(--primary)', fontSize: '14px',
                          boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
                        }}>
                          ₱{parseFloat(room.price).toLocaleString()}
                        </div>
                        <div style={{
                          position: 'absolute', top: '12px', left: '12px',
                          background: 'rgba(0,0,0,0.45)', backdropFilter: 'blur(6px)',
                          padding: '4px 10px', borderRadius: '8px',
                          fontWeight: 700, color: 'white', fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.5px'
                        }}>
                          {room.category}
                        </div>
                        <div style={{ position: 'absolute', bottom: '12px', left: '14px', right: '14px' }}>
                          <h4 style={{ margin: 0, color: 'white', fontSize: '17px', fontWeight: 900, textShadow: '0 1px 4px rgba(0,0,0,0.4)' }}>
                            {room.title}{room.nickname ? ` · ${room.nickname}` : ''}
                          </h4>
                          <p style={{ margin: '2px 0 0 0', color: 'rgba(255,255,255,0.8)', fontSize: '12px', fontWeight: 600 }}>{room.location}</p>
                        </div>
                      </div>

                      <div style={{ padding: '16px 18px' }}>
                        {room.inclusions && room.inclusions.length > 0 && (
                          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginBottom: '14px' }}>
                            {(Array.isArray(room.inclusions) ? room.inclusions : Object.values(room.inclusions)).slice(0, 3).map((inc, i) => (
                              <span key={i} style={{ background: 'var(--secondary-soft)', color: 'var(--secondary)', fontSize: '11px', fontWeight: 700, padding: '3px 9px', borderRadius: '8px' }}>{inc}</span>
                            ))}
                            {(Array.isArray(room.inclusions) ? room.inclusions : Object.values(room.inclusions)).length > 3 && (
                              <span style={{ background: '#F1F5F9', color: 'var(--text-muted)', fontSize: '11px', fontWeight: 700, padding: '3px 9px', borderRadius: '8px' }}>+{(Array.isArray(room.inclusions) ? room.inclusions : Object.values(room.inclusions)).length - 3} more</span>
                            )}
                          </div>
                        )}
                        <div style={{ display: 'flex', gap: '10px' }}>
                          <button
                            className="btn"
                            style={{ flex: 1, padding: '9px', background: 'var(--light-bg)', color: 'var(--text-main)', borderRadius: '12px', fontSize: '13px', border: '1px solid var(--border)' }}
                            onClick={() => { setRoomToEdit(room); setShowAddRoom(true); }}
                          >
                            <Edit3 size={15} /> Edit
                          </button>
                          <button
                            className="btn"
                            style={{ flex: 1, padding: '9px', background: isConfirmingDelete ? '#FEE2E2' : 'rgba(239, 68, 68, 0.1)', color: 'var(--primary)', borderRadius: '12px', fontSize: '13px', border: isConfirmingDelete ? '1.5px solid #FECACA' : 'none' }}
                            onClick={() => setRoomToDelete(isConfirmingDelete ? null : room.id)}
                          >
                            <Trash2 size={15} /> {isConfirmingDelete ? 'Cancel' : 'Delete'}
                          </button>
                        </div>

                        <div style={{ marginTop: '14px', borderTop: '1px solid var(--border)', paddingTop: '14px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <span style={{ fontSize: '13px', fontWeight: 700, color: room.isDisabled ? 'var(--text-muted)' : 'var(--text-main)' }}>
                            {room.isDisabled ? 'Room Disabled' : 'Available to Book'}
                          </span>
                          <button
                            className="btn"
                            style={{
                              padding: '6px 12px',
                              fontSize: '12px',
                              borderRadius: '8px',
                              background: room.isDisabled ? 'var(--light-bg)' : 'var(--primary-soft)',
                              color: room.isDisabled ? 'var(--text-muted)' : 'var(--primary)',
                              border: room.isDisabled ? '1px solid var(--border)' : '1px solid rgba(251, 54, 64, 0.2)',
                            }}
                            onClick={() => handleDisableRoomClick(room)}
                          >
                            {room.isDisabled ? 'Enable Room' : 'Disable Room'}
                          </button>
                        </div>
                      </div>

                      {isConfirmingDelete && (
                        <div className="delete-confirm-panel" style={{
                          background: 'linear-gradient(135deg, rgba(239, 68, 68, 0.1), #FFF5F5)',
                          borderTop: '1.5px solid #FEE2E2',
                          padding: '16px 18px',
                        }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '12px' }}>
                            <div style={{ background: '#FEE2E2', borderRadius: '50%', width: '32px', height: '32px', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                              <Trash2 size={15} color="#DC2626" />
                            </div>
                            <div>
                              <p style={{ margin: 0, fontWeight: 800, fontSize: '13px', color: '#DC2626' }}>Delete "{room.title}"?</p>
                              <p style={{ margin: 0, fontSize: '11px', color: 'var(--text-muted)', fontWeight: 600 }}>This action cannot be undone.</p>
                            </div>
                          </div>
                          <button
                            className="btn btn-danger"
                            style={{ width: '100%', padding: '10px', borderRadius: '12px', fontSize: '13px' }}
                            onClick={() => deleteRoom(room.id)}
                          >
                            Yes, Delete Permanently
                          </button>
                        </div>
                      )}
                    </div>
                  );
                })}
                {rooms.length > roomLimit && (
                  <div style={{ gridColumn: '1/-1', textAlign: 'center', marginTop: '8px' }}>
                    <button className="btn btn-secondary" onClick={() => setRoomLimit(prev => prev + 8)}>Load More Rooms</button>
                  </div>
                )}
              </>
            ) : (
              <div style={{ gridColumn: '1/-1', textAlign: 'center', padding: '80px 0', opacity: 0.45 }}>
                <HomeIcon size={52} style={{ marginBottom: '16px' }} />
                <p style={{ fontWeight: 700, fontSize: '16px' }}>No rooms in your inventory yet.</p>
                <p style={{ fontWeight: 500, fontSize: '13px', color: 'var(--text-muted)' }}>Click "Add New Room" to get started.</p>
              </div>
            )}
          </div>
        </section>
      )}

      {activeTab === 'Activities' && (
        <section className="view-transition">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px', flexWrap: 'wrap', gap: '16px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '16px', flexWrap: 'wrap' }}>
              <h3 style={{ margin: 0, fontSize: '22px', fontWeight: 800 }}>Activities</h3>
              {/* Show/Hide Activities on Property Toggle */}
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '10px',
                background: showActivities ? 'rgba(29, 211, 176, 0.1)' : 'var(--light-bg)',
                border: `1px solid ${showActivities ? 'var(--secondary)' : 'var(--border)'}`,
                padding: '6px 14px',
                borderRadius: '12px'
              }}>
                <span style={{ fontSize: '13px', fontWeight: 700, color: showActivities ? 'var(--text-main)' : 'var(--text-muted)' }}>
                  {showActivities ? '🟢 Section Visible to Guests' : '⚪ Section Hidden from Guests'}
                </span>
                <button
                  type="button"
                  onClick={toggleShowActivities}
                  disabled={updatingShowActivities}
                  style={{
                    background: showActivities ? '#EF4444' : 'var(--secondary)',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    padding: '4px 10px',
                    fontSize: '12px',
                    fontWeight: 800,
                    cursor: 'pointer'
                  }}
                >
                  {updatingShowActivities ? 'Updating...' : (showActivities ? 'Hide on Property' : 'Show on Property')}
                </button>
              </div>
            </div>
            <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
              <button
                className="btn"
                onClick={seedStandardActivities}
                style={{
                  background: 'var(--light-bg)',
                  border: '1px solid var(--border)',
                  color: 'var(--text-main)',
                  borderRadius: '14px',
                  padding: '10px 16px',
                  cursor: 'pointer',
                  fontWeight: 700,
                  fontSize: '13px'
                }}
                title="Populate Kayak (₱300), Paddle Board (₱300), Boatrides (₱1450/₱2000), and Karaoke (₱750)"
              >
                ⚡ Load Standard Activities
              </button>
              <button
                className="btn btn-secondary"
                onClick={() => { setActivityToEdit(null); setShowAddActivity(true); }}
                style={{ borderRadius: '14px', padding: '10px 20px', cursor: 'pointer' }}
              >
                <Plus size={18} /> Add New Activity
              </button>
            </div>
          </div>

          {/* Activity Operating Schedule Card */}
          <div style={{
            background: 'var(--surface)',
            border: '1px solid var(--border)',
            borderRadius: '16px',
            padding: '20px 24px',
            marginBottom: '28px',
            boxShadow: 'var(--shadow)',
            color: 'var(--text-main)'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '12px' }}>
              <div>
                <h4 style={{ margin: '0 0 6px 0', fontSize: '16px', fontWeight: 800, display: 'flex', alignItems: 'center', gap: '8px', color: 'var(--text-main)' }}>
                  🕒 Operating Hours & Schedule Notice for Guests
                </h4>
                <p style={{ margin: 0, fontSize: '13px', color: 'var(--text-muted)' }}>
                  This notice is displayed to guests when booking activities instead of fixed slot times.
                </p>
              </div>
              {!isEditingSchedule ? (
                <button
                  type="button"
                  className="btn"
                  onClick={() => setIsEditingSchedule(true)}
                  style={{
                    background: 'var(--light-bg)',
                    border: '1px solid var(--border)',
                    borderRadius: '10px',
                    padding: '8px 16px',
                    fontSize: '13px',
                    fontWeight: 700,
                    color: 'var(--text-main)',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                    cursor: 'pointer'
                  }}
                >
                  <Edit3 size={15} /> Edit Schedule
                </button>
              ) : (
                <div style={{ display: 'flex', gap: '8px' }}>
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={saveActivitySchedule}
                    disabled={savingSchedule}
                    style={{ borderRadius: '10px', padding: '8px 16px', fontSize: '13px', fontWeight: 700, cursor: 'pointer' }}
                  >
                    {savingSchedule ? 'Saving...' : 'Save Schedule'}
                  </button>
                  <button
                    type="button"
                    className="btn"
                    onClick={() => setIsEditingSchedule(false)}
                    style={{ background: 'var(--light-bg)', border: '1px solid var(--border)', borderRadius: '10px', padding: '8px 14px', fontSize: '13px', color: 'var(--text-main)', cursor: 'pointer' }}
                  >
                    Cancel
                  </button>
                </div>
              )}
            </div>

            {isEditingSchedule ? (
              <div style={{ marginTop: '16px' }}>
                <textarea
                  value={activitySchedule}
                  onChange={(e) => setActivitySchedule(e.target.value)}
                  placeholder="e.g., Kayak, Boat ride to Pagsanjan falls, and Paddle board: 7:00 AM to 3:30 PM. Bar, Karaoke, and Dinner: 7:00 AM to 10:00 PM."
                  rows={3}
                  style={{
                    width: '100%',
                    padding: '12px 14px',
                    borderRadius: '12px',
                    border: '1px solid var(--border)',
                    fontSize: '14px',
                    background: 'var(--light-bg)',
                    color: 'var(--text-main)',
                    lineHeight: '1.5',
                    outline: 'none',
                    boxSizing: 'border-box'
                  }}
                />
                <span style={{ fontSize: '12px', color: 'var(--text-muted)', display: 'block', marginTop: '6px' }}>
                  Tip: Leave blank to use the standard default hours.
                </span>
              </div>
            ) : (
              <div style={{
                marginTop: '14px',
                padding: '14px 18px',
                borderRadius: '12px',
                background: 'var(--secondary-soft)',
                border: '1px solid var(--secondary)',
                fontSize: '14px',
                color: 'var(--text-main)',
                fontWeight: 600,
                lineHeight: '1.6'
              }}>
                {activitySchedule && activitySchedule.trim()
                  ? activitySchedule
                  : "Kayak, Boat ride to Pagsanjan falls, and Paddle board: 7:00 AM to 3:30 PM. Bar, Karaoke, and Dinner: 7:00 AM to 10:00 PM. (Default)"}
              </div>
            )}
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: '24px' }}>
            {activities.length > 0 ? activities.map(activity => {
              const imgSrc = (Array.isArray(activity.imageUrls) ? activity.imageUrls[0] : null) || 'https://via.placeholder.com/400x200?text=No+Photo';
              const slots = Array.isArray(activity.timeSlots) ? activity.timeSlots : [];
              return (
                <div key={activity.id} className="room-card">
                  <div style={{ position: 'relative', overflow: 'hidden', borderRadius: '16px 16px 0 0' }}>
                    <img src={imgSrc} alt={activity.title} style={{ width: '100%', height: '200px', objectFit: 'cover' }} />
                    <div style={{ position: 'absolute', top: '12px', right: '12px', background: 'var(--secondary)', color: 'white', padding: '6px 14px', borderRadius: '20px', fontWeight: 800, fontSize: '14px' }}>₱{Number(activity.price || 0).toLocaleString()}/pax</div>
                  </div>
                  <div style={{ padding: '20px' }}>
                    <h4 style={{ margin: '0 0 8px 0', fontWeight: 800, fontSize: '18px' }}>{activity.title || 'Untitled Activity'}</h4>
                    <p style={{ margin: '0 0 12px 0', fontSize: '13px', color: 'var(--text-muted)' }}>{activity.description ? activity.description.substring(0, 80) + (activity.description.length > 80 ? '...' : '') : 'No description'}</p>
                    <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginBottom: '12px' }}>
                      <span style={{ background: 'var(--light-bg)', padding: '4px 10px', borderRadius: '8px', fontSize: '12px', fontWeight: 700 }}>Max: {activity.maxPax || '?'} pax</span>
                      {slots.slice(0, 3).map((s, i) => (
                        <span key={i} style={{ background: 'rgba(29,211,176,0.12)', color: 'var(--secondary)', padding: '4px 10px', borderRadius: '8px', fontSize: '12px', fontWeight: 700 }}>{s}</span>
                      ))}
                      {slots.length > 3 && <span style={{ padding: '4px 10px', borderRadius: '8px', fontSize: '12px', fontWeight: 700, color: 'var(--text-muted)' }}>+{slots.length - 3} more</span>}
                    </div>
                    <div style={{ display: 'flex', gap: '8px' }}>
                      <button className="btn" style={{ flex: 1, background: 'var(--light-bg)', border: '1px solid var(--border)', color: 'var(--text-main)', borderRadius: '10px', padding: '8px' }} onClick={() => { setActivityToEdit(activity); setShowAddActivity(true); }}>
                        <Edit3 size={15} /> Edit
                      </button>
                      <button className="btn" style={{ background: '#FEE2E2', border: '1px solid #FECACA', color: '#EF4444', borderRadius: '10px', padding: '8px 14px' }} onClick={() => { if (window.confirm(`Delete "${activity.title}"?`)) { remove(ref(db, `properties/${uid}/activities/${activity.id}`)); } }}>
                        <Trash2 size={15} />
                      </button>
                    </div>
                  </div>
                </div>
              );
            }) : (
              <div style={{ gridColumn: '1/-1', textAlign: 'center', padding: '80px 0', opacity: 0.45 }}>
                <Calendar size={52} style={{ marginBottom: '16px' }} />
                <p style={{ fontWeight: 700, fontSize: '16px' }}>No activities listed yet.</p>
                <p style={{ fontWeight: 500, fontSize: '13px', color: 'var(--text-muted)' }}>Click "Add New Activity" to get started.</p>
              </div>
            )}
          </div>
        </section>
      )}

      {activeTab === 'Food Menu' && (
        <section className="view-transition">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
            <h3 style={{ margin: 0, fontSize: '22px', fontWeight: 800 }}>Food Menu Management</h3>
          </div>
          
          <div style={{ background: 'var(--surface)', padding: '24px', borderRadius: '24px', marginBottom: '32px' }}>
            {enableCustomMenuUpload ? (
              <>
                <p style={{ margin: '0 0 16px 0', fontSize: '14px', color: 'var(--text-muted)' }}>
                  Upload your property's food menu images (Breakfast, Lunch, Dinner). Guests can view these images directly.
                </p>
                
                <div style={{ marginBottom: '24px' }}>
                  <label style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', background: 'var(--primary-soft)', color: 'var(--primary)', padding: '12px 24px', borderRadius: '12px', cursor: 'pointer', fontWeight: 800 }}>
                    {isUploadingMenu ? 'Uploading...' : 'Upload Menu Images'}
                    <input type="file" multiple accept="image/*" style={{ display: 'none' }} disabled={isUploadingMenu} onChange={async (e) => {
                      const files = Array.from(e.target.files);
                      if (files.length === 0) return;
                      setIsUploadingMenu(true);
                      const newUrls = [...foodMenuUrls];
                      for (const file of files) {
                        const data = new FormData();
                        data.append('file', file);
                        data.append('upload_preset', 'resort_unsigned');
                        try {
                          const response = await fetch('https://api.cloudinary.com/v1_1/dnv6ezitm/image/upload', {
                            method: 'POST',
                            body: data,
                          });
                          const res = await response.json();
                          newUrls.push(res.secure_url);
                        } catch (error) {
                          console.error('Upload failed', error);
                        }
                      }
                      await update(ref(db, `properties/${uid}`), { foodMenuUrls: newUrls });
                      setFoodMenuUrls(newUrls);
                      setIsUploadingMenu(false);
                    }} />
                  </label>
                </div>

                {foodMenuUrls.length > 0 ? (
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: '16px' }}>
                    {foodMenuUrls.map((url, i) => (
                      <div key={i} style={{ position: 'relative', borderRadius: '16px', overflow: 'hidden', height: '200px' }}>
                        <img src={url} alt={`Menu ${i}`} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                        <button
                          onClick={async () => {
                            if (window.confirm('Delete this menu image?')) {
                              const newUrls = foodMenuUrls.filter((_, index) => index !== i);
                              await update(ref(db, `properties/${uid}`), { foodMenuUrls: newUrls });
                              setFoodMenuUrls(newUrls);
                            }
                          }}
                          style={{
                            position: 'absolute', top: '8px', right: '8px', background: 'rgba(239, 68, 68, 0.9)', color: 'white',
                            border: 'none', borderRadius: '50%', width: '32px', height: '32px', cursor: 'pointer',
                            display: 'flex', alignItems: 'center', justifyContent: 'center'
                          }}
                        >
                          <X size={16} />
                        </button>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div style={{ textAlign: 'center', padding: '40px', background: 'var(--light-bg)', borderRadius: '16px', color: 'var(--text-muted)' }}>
                    No menu images uploaded yet.
                  </div>
                )}
              </>
            ) : (
              <div style={{ textAlign: 'center', padding: '40px', background: 'var(--light-bg)', borderRadius: '16px', color: 'var(--text-muted)' }}>
                <p style={{ margin: 0, fontWeight: 500, fontSize: '15px' }}>Defaulting to standard interactive (+ / -) meal quantity counters during booking.</p>
                <p style={{ margin: '8px 0 0 0', fontSize: '13px' }}>Enable Custom Food Menu Upload in Property Settings to upload your own digital menu images.</p>
              </div>
            )}
          </div>
        </section>
      )}

      {activeTab === 'Bookings' && (
        <section className="view-transition">
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '24px', flexWrap: 'wrap', gap: '16px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <Calendar size={20} color="var(--primary)" />
              <h3 style={{ margin: 0, fontSize: '22px', fontWeight: 800 }}>Reservations</h3>
            </div>

            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <span style={{ fontSize: '13px', fontWeight: 600, color: 'var(--text-muted)' }}>Filter:</span>
              <select
                className="input"
                style={{ width: 'auto', minWidth: '160px', padding: '8px 12px', fontSize: '13px', borderRadius: '12px' }}
                value={bookingFilter}
                onChange={e => { setBookingFilter(e.target.value); setBookingLimit(10); }}
              >
                <option value="All">All Reservations ({bookings.length})</option>
                <option value="Pending">Pending ({bookings.filter(b => b.status === 'Pending').length})</option>
                <option value="Confirmed">Confirmed ({bookings.filter(b => b.status === 'Confirmed').length})</option>
                <option value="Checked In">Checked In ({bookings.filter(b => b.status === 'Checked In').length})</option>
                <option value="Completed">Completed ({bookings.filter(b => b.status === 'Completed').length})</option>
                <option value="Imported">Imported Historical ({bookings.filter(b => b.isHistorical === true).length})</option>
                <option value="Reschedule Requested">Reschedule Requests ({bookings.filter(b => b.status === 'Reschedule Requested').length})</option>
                <option value="Refund Requested">Refund Requests ({bookings.filter(b => b.status === 'Refund Requested').length})</option>
                <option value="Refund Approved">Refund Approved ({bookings.filter(b => b.status === 'Refund Approved').length})</option>
                <option value="Refund Confirmed">Refund Confirmed ({bookings.filter(b => b.status === 'Refund Confirmed').length})</option>
                <option value="Refund Declined">Refund Declined ({bookings.filter(b => b.status === 'Refund Declined').length})</option>
                <option value="No Show">No Show ({bookings.filter(b => (b.status || '').toLowerCase() === 'no show').length})</option>
                <option value="Cancelled">Declined / Cancelled ({bookings.filter(b => ['Cancelled', 'Declined'].includes(b.status)).length})</option>
              </select>
            </div>
          </div>

          <div className="card" style={{ padding: 0, overflow: 'hidden', border: '1px solid var(--border)', boxShadow: 'var(--shadow)', borderRadius: '20px', width: '100%' }}>
            {(() => {
              const filteredBookings = bookings.filter(b => {
                if (bookingFilter === 'All') return true;
                if (bookingFilter === 'Imported') return b.isHistorical === true;
                if (bookingFilter === 'Cancelled') return ['Cancelled', 'Declined'].includes(b.status);
                if (bookingFilter === 'No Show') return (b.status || '').toLowerCase() === 'no show';
                return b.status === bookingFilter;
              }).sort((a, b) => {
                if (bookingFilter === 'All') {
                  const attentionStatuses = ['Pending', 'Reschedule Requested', 'Refund Requested'];
                  const aNeedsAttention = attentionStatuses.includes(a.status);
                  const bNeedsAttention = attentionStatuses.includes(b.status);
                  if (aNeedsAttention && !bNeedsAttention) return -1;
                  if (!aNeedsAttention && bNeedsAttention) return 1;
                }
                return 0;
              });

              if (filteredBookings.length === 0) {
                return <p style={{ textAlign: 'center', color: 'var(--text-muted)', padding: '60px 0' }}>No {bookingFilter === 'All' ? '' : bookingFilter.toLowerCase()} bookings found.</p>;
              }

              return (
                <div style={{ overflowX: 'auto', width: '100%' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left', minWidth: '850px' }}>
                    <thead>
                      <tr style={{ background: 'var(--light-bg)', borderBottom: '1px solid var(--border)', height: '48px' }}>
                        <th style={{ padding: '12px 18px', fontSize: '12px', fontWeight: 800, textTransform: 'uppercase', color: 'var(--text-muted)', letterSpacing: '0.8px' }}>Guest</th>
                        <th style={{ padding: '12px 18px', fontSize: '12px', fontWeight: 800, textTransform: 'uppercase', color: 'var(--text-muted)', letterSpacing: '0.8px' }}>Item / Room</th>
                        <th style={{ padding: '12px 18px', fontSize: '12px', fontWeight: 800, textTransform: 'uppercase', color: 'var(--text-muted)', letterSpacing: '0.8px' }}>Schedule & Duration</th>
                        <th style={{ padding: '12px 18px', fontSize: '12px', fontWeight: 800, textTransform: 'uppercase', color: 'var(--text-muted)', letterSpacing: '0.8px' }}>Total Amount</th>
                        <th style={{ padding: '12px 18px', fontSize: '12px', fontWeight: 800, textTransform: 'uppercase', color: 'var(--text-muted)', letterSpacing: '0.8px' }}>Status</th>
                        <th style={{ padding: '12px 18px', fontSize: '12px', fontWeight: 800, textTransform: 'uppercase', color: 'var(--text-muted)', letterSpacing: '0.8px', textAlign: 'center' }}>Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredBookings.slice(0, bookingLimit).map(booking => {
                        const hasConflict = booking.status === 'Pending' && checkConflict(booking, bookings);
                        let isOverdue = false;
                        const bookingDateVal = booking.bookingDate || booking.checkInDate || booking.date;
                        if (booking.status === 'Checked In' && bookingDateVal) {
                          try {
                            const parsed = parseDateSafely(bookingDateVal);
                            if (parsed) {
                              const endDate = addDays(parsed, parseInt(booking.nights) || 1);
                              const todayMidnight = new Date();
                              todayMidnight.setHours(0, 0, 0, 0);
                              if (todayMidnight > endDate || todayMidnight.getTime() === endDate.getTime()) {
                                isOverdue = true;
                              }
                            }
                          } catch (e) {}
                        }

                        const guestName = booking.touristName || booking.customerName || booking.userName || booking.name || booking.fullName || 'Guest';
                        const itemTitle = booking.activityTitle || booking.roomTitle || 'Room / Activity';
                        const isAct = booking.isActivityBooking === true || (
                          !booking.roomId &&
                          !booking.roomTitle &&
                          !(booking.activityTitle && String(booking.activityTitle).toLowerCase().includes('room')) &&
                          !booking.nights &&
                          Boolean(booking.activityId && String(booking.activityId).trim() !== '')
                        );
                        const duration = parseInt(booking.hours || booking.nights || 1);
                        const durationStr = `${duration} ${isAct ? 'Hour/s' : (duration === 1 ? 'Night' : 'Nights')}`;

                        return (
                          <tr
                            key={booking.id}
                            className="table-row-interactive"
                            onClick={() => { setScannedViaQr(false); setScannedBooking(booking); }}
                            style={{
                              borderBottom: '1px solid var(--border)',
                              cursor: 'pointer',
                              transition: 'background 0.15s ease',
                              background: isOverdue ? 'rgba(239, 68, 68, 0.04)' : (hasConflict ? 'rgba(251, 54, 64, 0.04)' : 'transparent')
                            }}
                          >
                            <td style={{ padding: '14px 18px' }}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                <div style={{
                                  width: '38px', height: '38px', borderRadius: '12px', background: 'var(--light-bg)',
                                  display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--secondary)',
                                  fontWeight: 800, flexShrink: 0, overflow: 'hidden'
                                }}>
                                  {guestName.charAt(0).toUpperCase()}
                                </div>
                                <div>
                                  <div style={{ fontWeight: 800, fontSize: '14px', color: 'var(--text-main)', display: 'flex', alignItems: 'center', gap: '6px' }}>
                                    <span>{guestName}</span>
                                    {booking.isHistorical && (
                                      <span style={{ fontSize: '10px', fontWeight: 800, color: 'var(--secondary)', background: 'rgba(29, 211, 176, 0.12)', padding: '1px 6px', borderRadius: '8px' }}>Imported</span>
                                    )}
                                  </div>
                                  <div style={{ fontSize: '12px', color: 'var(--text-muted)' }}>
                                    Ref: #{booking.id.slice(-6).toUpperCase()}
                                  </div>
                                </div>
                              </div>
                            </td>

                            <td style={{ padding: '14px 18px' }}>
                              <div style={{ fontWeight: 700, fontSize: '13.5px', color: 'var(--text-main)' }}>{itemTitle}</div>
                              {booking.paymentOption && (
                                <div style={{ fontSize: '11.5px', color: 'var(--primary)', fontWeight: 600, marginTop: '2px', display: 'flex', alignItems: 'center', gap: '4px' }}>
                                  <CreditCard size={12} /> {booking.paymentOption}
                                </div>
                              )}
                            </td>

                            <td style={{ padding: '14px 18px' }}>
                              <div style={{ fontWeight: 600, fontSize: '13px', color: 'var(--text-main)' }}>
                                {booking.isHistorical === true ? (
                                  (() => {
                                    const pStart = parseDateSafely(booking.bookingDate || booking.checkInDate || booking.date);
                                    return pStart ? format(pStart, 'MMM dd, yyyy') : (booking.bookingDate || 'N/A');
                                  })()
                                ) : (
                                  formatBookingDateRange(booking)
                                )}
                              </div>
                              <div style={{ fontSize: '12px', color: 'var(--text-muted)', display: 'flex', alignItems: 'center', gap: '6px', marginTop: '2px' }}>
                                <span>{durationStr}</span>
                                {(booking.arrivalTime || booking.timeSlot || booking.bookingTime) && (
                                  <>
                                    <span>•</span>
                                    <span>{booking.arrivalTime || booking.timeSlot || booking.bookingTime}</span>
                                  </>
                                )}
                              </div>
                            </td>

                            <td style={{ padding: '14px 18px' }}>
                              <span style={{ fontWeight: 800, fontSize: '15px', color: 'var(--secondary)' }}>
                                ₱{Number(booking.totalPrice || 0).toLocaleString()}
                              </span>
                              {Boolean((booking.gcashReceipt && booking.gcashReceipt !== 'MANUAL_GCASH_PAYMENT') || booking.historicalPhotoUrl || booking.paymentReceiptDataUrl) && (
                                <div style={{ fontSize: '11px', color: '#2563EB', fontWeight: 700, marginTop: '2px', display: 'flex', alignItems: 'center', gap: '3px' }}>
                                  <Camera size={11} /> Receipt Attached
                                </div>
                              )}
                            </td>

                            <td style={{ padding: '14px 18px' }}>
                              <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', alignItems: 'flex-start' }}>
                                <span className={`status-badge status-${(booking.status || 'pending').toLowerCase().replace(' ', '-')}`} style={{ fontSize: '11px', padding: '4px 10px', borderRadius: '10px' }}>
                                  {booking.status || 'Pending'}
                                </span>
                                {isOverdue && (
                                  <span style={{ background: '#EF4444', color: 'white', fontSize: '10px', fontWeight: 800, padding: '2px 6px', borderRadius: '6px' }}>
                                    Overdue
                                  </span>
                                )}
                                {hasConflict && (
                                  <span style={{ background: 'rgba(251, 54, 64, 0.15)', color: 'var(--primary)', fontSize: '10px', fontWeight: 800, padding: '2px 6px', borderRadius: '6px' }}>
                                    Conflict
                                  </span>
                                )}
                              </div>
                            </td>

                            <td style={{ padding: '14px 18px', textAlign: 'center' }}>
                              <button
                                className="btn"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setScannedViaQr(false);
                                  setScannedBooking(booking);
                                }}
                                style={{
                                  padding: '6px 14px',
                                  fontSize: '12px',
                                  fontWeight: 700,
                                  borderRadius: '10px',
                                  background: 'rgba(79, 70, 229, 0.1)',
                                  color: '#6366F1',
                                  border: '1px solid rgba(79, 70, 229, 0.2)',
                                  cursor: 'pointer',
                                  display: 'inline-flex',
                                  alignItems: 'center',
                                  gap: '5px'
                                }}
                              >
                                <Eye size={13} /> View
                              </button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>

                  {filteredBookings.length > bookingLimit && (
                    <div style={{ padding: '16px', textAlign: 'center', borderTop: '1px solid var(--border)' }}>
                      <button className="btn btn-secondary" style={{ padding: '10px 24px', borderRadius: '12px' }} onClick={() => setBookingLimit(prev => prev + 10)}>
                        Load More Reservations ({filteredBookings.length - bookingLimit} remaining)
                      </button>
                    </div>
                  )}
                </div>
              );
            })()}
          </div>
        </section>
      )}

      {activeTab === 'Balances' && (
        <section className="view-transition">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <CreditCard size={20} color="var(--primary)" />
              <h3 style={{ margin: 0, fontSize: '22px', fontWeight: 800 }}>Unpaid Balances</h3>
            </div>
            <p style={{ margin: '8px 0 0 0', color: 'var(--text-muted)', fontSize: '13px', lineHeight: '1.5' }}>
              Use this section to manually mark a booking as fully paid if a tourist decides to pay their remaining balance early (e.g., via PM) before arriving at the resort.
            </p>
            <div style={{ position: 'relative', width: '300px' }}>
              <Search size={18} color="var(--text-muted)" style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)' }} />
              <input
                type="text"
                placeholder="Search name, booking ID, or date (e.g. Jul 19)..."
                className="input"
                style={{ width: '100%', paddingLeft: '40px', borderRadius: '12px' }}
                value={balanceSearchQuery}
                onChange={(e) => setBalanceSearchQuery(e.target.value)}
              />
            </div>
          </div>

          <div style={{ display: 'grid', gap: '20px' }}>
            {balancesByTourist.length > 0 ? balancesByTourist.map(group => (
              <div key={group.touristUid} className="card" style={{ padding: '24px', borderRadius: '24px', border: '1px solid rgba(0,0,0,0.05)' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '20px', paddingBottom: '16px', borderBottom: '1px dashed var(--border)' }}>
                  <div>
                    <h4 style={{ margin: '0 0 4px 0', fontSize: '18px', fontWeight: 800 }}>{group.touristName}</h4>
                    <p style={{ margin: 0, fontSize: '13px', color: 'var(--text-muted)', fontWeight: 600 }}>Total Unpaid Balance: <span style={{ color: 'var(--primary)', fontWeight: 800 }}>₱{group.totalBalance.toLocaleString()}</span></p>
                  </div>
                  {group.bookings.length > 1 && (
                    <div style={{ display: 'flex', gap: '8px' }}>
                      {group.bookings.some(b => selectedBalances[b.id]) && (
                        <button 
                          className="btn" 
                          style={{ background: '#3B82F6', color: 'white', padding: '8px 16px', fontSize: '12px' }}
                          onClick={() => initiatePaymentAction('selected', group)}
                        >
                          Mark Selected Paid
                        </button>
                      )}
                      <button 
                        className="btn" 
                        style={{ background: '#10B981', color: 'white', padding: '8px 16px', fontSize: '12px' }}
                        onClick={() => initiatePaymentAction('all', group)}
                      >
                        Mark All Paid
                      </button>
                    </div>
                  )}
                </div>
                
                <div style={{ display: 'grid', gap: '12px' }}>
                  {group.bookings.map(b => {
                    const bal = getBalance(b);
                    return (
                      <div key={b.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: 'var(--light-bg)', padding: '16px', borderRadius: '16px', border: '1px solid var(--border)' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                          <input 
                            type="checkbox" 
                            checked={!!selectedBalances[b.id]} 
                            onChange={() => toggleBalanceSelection(b.id)}
                            style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                          />
                          <div>
                            <p style={{ margin: '0 0 4px 0', fontWeight: 700, fontSize: '14px' }}>{b.activityTitle || b.roomTitle}</p>
                            <p style={{ margin: 0, fontSize: '12px', color: 'var(--text-muted)' }}>Booking Ref: {b.id.slice(-6).toUpperCase()} • {b.bookingDate || b.checkInDate || b.date || 'N/A'}</p>
                            <p style={{ margin: '4px 0 0 0', fontSize: '13px', fontWeight: 800, color: 'var(--primary)' }}>Balance: ₱{bal.toLocaleString()}</p>
                          </div>
                        </div>
                        <button 
                          className="btn" 
                          style={{ background: 'rgba(16, 185, 129, 0.1)', color: '#059669', padding: '8px 16px', fontSize: '12px', border: '1px solid rgba(16, 185, 129, 0.2)' }}
                          onClick={() => initiatePaymentAction('single', { bookingId: b.id, totalPrice: b.totalPrice })}
                        >
                          Mark as Paid
                        </button>
                      </div>
                    );
                  })}
                </div>
              </div>
            )) : (
              <div style={{ textAlign: 'center', padding: '60px 20px', background: 'var(--surface)', borderRadius: '24px', border: '1px dashed var(--border)' }}>
                <CheckCircle2 size={40} color="#10B981" style={{ marginBottom: '16px' }} />
                <h4 style={{ margin: '0 0 8px 0', fontSize: '18px', fontWeight: 800 }}>All Settled!</h4>
                <p style={{ margin: 0, color: 'var(--text-muted)', fontSize: '14px' }}>No active bookings with unpaid balances.</p>
              </div>
            )}
          </div>
        </section>
      )}

      {activeTab === 'Reviews' && (
        <section className="view-transition">
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '24px', flexWrap: 'wrap', gap: '16px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
              <div style={{ width: '6px', height: '24px', background: '#F59E0B', borderRadius: '10px' }}></div>
              <h3 style={{ margin: 0, fontSize: '22px', fontWeight: 800 }}>Guest Reviews</h3>
              <span style={{ fontSize: '13px', background: 'var(--light-bg)', padding: '4px 10px', borderRadius: '12px', fontWeight: 700, color: 'var(--text-muted)' }}>
                {reviews.length} {reviews.length === 1 ? 'Review' : 'Reviews'}
              </span>
              {reviews.some(r => !readReviews[r.id]) && (
                <button
                  onClick={markAllReviewsAsRead}
                  style={{
                    background: 'transparent',
                    border: '1px solid var(--border)',
                    padding: '4px 12px',
                    borderRadius: '12px',
                    fontSize: '12px',
                    fontWeight: 700,
                    color: 'var(--primary)',
                    cursor: 'pointer'
                  }}
                >
                  Mark all as read
                </button>
              )}
            </div>

            {reviews.length > 0 && (() => {
              const avg = reviews.reduce((sum, r) => sum + (parseFloat(r.rating) || 0), 0) / reviews.length;
              return (
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', background: 'var(--surface)', padding: '8px 18px', borderRadius: '16px', border: '1px solid var(--border)', boxShadow: '0 2px 8px rgba(0,0,0,0.04)' }}>
                  <Star size={18} fill="#FFD700" color="#FFD700" />
                  <span style={{ fontSize: '18px', fontWeight: 900, color: 'var(--text-main)' }}>{avg.toFixed(1)}</span>
                  <span style={{ fontSize: '12px', color: 'var(--text-muted)', fontWeight: 600 }}>out of 5.0</span>
                </div>
              );
            })()}
          </div>

          {reviews.length > 0 ? (
            <div
              style={{
                columnCount: 'auto',
                columnWidth: '320px',
                columnGap: '20px',
                width: '100%',
                marginBottom: '32px'
              }}
            >
              {reviews.map((r, i) => {
                const isUnread = !readReviews[r.id];

                // Reply action buttons (Edit / Delete)
                const replyActions = r.ownerReply ? (
                  <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setReplyingReviewId(r.id);
                        setReplyText(r.ownerReply);
                      }}
                      style={{
                        background: 'none',
                        border: 'none',
                        color: 'var(--text-muted)',
                        fontSize: '11px',
                        fontWeight: 700,
                        cursor: 'pointer',
                        padding: 0,
                        textDecoration: 'underline'
                      }}
                    >
                      Edit
                    </button>
                    <button
                      onClick={async (e) => {
                        e.stopPropagation();
                        if (!window.confirm("Are you sure you want to delete your reply? The guest's review will stay.")) return;
                        try {
                          await update(ref(db, `reviews/${uid}/${r.id}`), {
                            ownerReply: null,
                            ownerReplyTimestamp: null
                          });
                        } catch (err) {
                          alert('Failed to delete reply: ' + err.message);
                        }
                      }}
                      style={{
                        background: 'none',
                        border: 'none',
                        color: '#EF4444',
                        fontSize: '11px',
                        fontWeight: 700,
                        cursor: 'pointer',
                        padding: 0,
                        textDecoration: 'underline'
                      }}
                    >
                      Delete
                    </button>
                  </div>
                ) : null;

                // Reply input box (when owner clicks reply or edit)
                const replyInput = replyingReviewId === r.id ? (
                  <div style={{ marginTop: '10px', paddingTop: '10px', borderTop: '1px dashed var(--border)' }} onClick={(e) => e.stopPropagation()}>
                    <textarea
                      value={replyText}
                      onChange={(e) => setReplyText(e.target.value)}
                      placeholder="Write a response to this guest review..."
                      rows={3}
                      style={{
                        width: '100%',
                        padding: '10px',
                        borderRadius: '12px',
                        border: '1px solid var(--border)',
                        background: 'var(--background)',
                        color: 'var(--text-main)',
                        fontSize: '13px',
                        resize: 'none',
                        fontFamily: 'inherit',
                        boxSizing: 'border-box'
                      }}
                    />
                    <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px', marginTop: '8px' }}>
                      <button
                        type="button"
                        onClick={() => {
                          setReplyingReviewId(null);
                          setReplyText('');
                        }}
                        className="btn"
                        style={{ padding: '6px 14px', fontSize: '12px', borderRadius: '10px' }}
                      >
                        Cancel
                      </button>
                      <button
                        type="button"
                        disabled={submittingReply || !replyText.trim()}
                        onClick={async () => {
                          if (!replyText.trim()) return;
                          setSubmittingReply(true);
                          try {
                            await update(ref(db, `reviews/${uid}/${r.id}`), {
                              ownerReply: replyText.trim(),
                              ownerReplyTimestamp: Date.now()
                            });
                            setReplyingReviewId(null);
                            setReplyText('');
                          } catch (err) {
                            alert('Failed to post reply: ' + err.message);
                          } finally {
                            setSubmittingReply(false);
                          }
                        }}
                        className="btn btn-primary"
                        style={{ padding: '6px 14px', fontSize: '12px', borderRadius: '10px', fontWeight: 800 }}
                      >
                        {submittingReply ? 'Posting...' : 'Post Reply'}
                      </button>
                    </div>
                  </div>
                ) : (
                  !r.ownerReply ? (
                    <div style={{ marginTop: '4px' }}>
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          setReplyingReviewId(r.id);
                          setReplyText('');
                        }}
                        style={{
                          background: 'transparent',
                          border: '1px solid var(--border)',
                          borderRadius: '10px',
                          padding: '5px 12px',
                          fontSize: '12px',
                          fontWeight: 700,
                          color: 'var(--primary)',
                          cursor: 'pointer',
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '6px'
                        }}
                      >
                        💬 Reply to Guest
                      </button>
                    </div>
                  ) : null
                );

                return (
                  <CompactReviewCard
                    key={r.id || i}
                    review={r}
                    isUnread={isUnread}
                    showDate={true}
                    onCardClick={() => markReviewAsRead(r.id)}
                    replyActions={replyActions}
                    replyInput={replyInput}
                    onPhotoClick={(photos, pIdx) => {
                      setViewerPhotos(photos);
                      setViewerIndex(pIdx);
                    }}
                  />
                );
              })}
            </div>
          ) : (
            <div style={{ textAlign: 'center', padding: '60px 20px', background: 'var(--surface)', borderRadius: '24px', border: '1px dashed var(--border)', marginBottom: '32px' }}>
              <Star size={40} color="#F59E0B" style={{ marginBottom: '16px', opacity: 0.5 }} />
              <h4 style={{ margin: '0 0 8px 0', fontSize: '18px', fontWeight: 800 }}>No Guest Reviews Yet</h4>
              <p style={{ margin: 0, color: 'var(--text-muted)', fontSize: '14px' }}>Reviews submitted by guests after their stay will be organized here.</p>
            </div>
          )}
        </section>
      )}

      {activeTab === 'Chat' && (
        <section className="view-transition">
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '24px' }}>
            <MessageSquare size={20} color="var(--secondary)" />
            <h3 style={{ margin: 0, fontSize: '22px', fontWeight: 800 }}>Inquiries</h3>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(350px, 1fr))', gap: '16px' }}>
            {chatRooms.length > 0 ? chatRooms.map(room => (
              <ChatRoomItem key={room.otherUid} room={room} onClick={setSelectedChat} />
            )) : (
              <div style={{ gridColumn: '1/-1', textAlign: 'center', padding: '80px 0', opacity: 0.5 }}>
                <MessageSquare size={48} style={{ marginBottom: '16px' }} />
                <p style={{ fontWeight: 600 }}>No active conversations.</p>
              </div>
            )}
          </div>
        </section>
      )}

      {activeTab === 'Import Data' && (
        <section className="view-transition">
          <HistoricalDataImport profile={profile} uid={uid} onBack={() => setActiveTab('Bookings')} />
        </section>
      )}

      {showRevenue && (
        <div className="modal-overlay" onClick={() => { setShowRevenue(false); setExpandedMonth(null); }} style={{ zIndex: 2000 }}>
          <style>{`
            @media print {
              body * { visibility: hidden; }
              .print-area, .print-area * { visibility: visible; }
              .print-area { position: absolute; left: 0; top: 0; width: 100%; height: auto; }
              .no-print { display: none !important; }
              .modal-content { max-height: none !important; overflow: visible !important; border: none !important; padding: 0 !important; margin: 0 !important; }
            }
          `}</style>
          <div className="card modal-content print-area" onClick={e => e.stopPropagation()} style={{ maxWidth: expandedMonth ? '600px' : '550px', borderRadius: '32px', padding: '32px', transition: 'all 0.3s ease', maxHeight: '90vh', overflowY: 'auto' }}>
            {expandedMonth ? (
              <>
                <div className="no-print" style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '24px' }}>
                  <button onClick={() => setExpandedMonth(null)} className="icon-btn"><ChevronRight size={20} style={{ transform: 'rotate(180deg)' }} /></button>
                  <h3 style={{ margin: 0, fontWeight: 800, flex: 1 }}>{expandedMonth} Report</h3>
                  <button onClick={() => handleCopyReport(expandedMonth)} className="btn btn-primary" style={{ padding: '8px 16px', borderRadius: '12px', fontSize: '13px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                    <Share2 size={16} /> Copy
                  </button>
                  <button onClick={() => window.print()} className="btn btn-secondary" style={{ padding: '8px 16px', borderRadius: '12px', fontSize: '13px', display: 'flex', alignItems: 'center', gap: '6px', border: '1px solid var(--border)' }}>
                    <Printer size={16} /> Print
                  </button>
                  <button onClick={() => { setShowRevenue(false); setExpandedMonth(null); }} className="close-btn"><X size={20} /></button>
                </div>

                <h3 className="print-only" style={{ display: 'none', margin: '0 0 20px 0', fontSize: '24px', fontWeight: 800 }}>{expandedMonth} Report</h3>
                <style>{`@media print { .print-only { display: block !important; } }`}</style>

                <div style={{ background: 'var(--surface)', padding: '24px', borderRadius: '24px', marginBottom: '24px', border: '1px solid var(--border)', textAlign: 'center', display: 'flex', justifyContent: 'space-around' }}>
                  <div>
                    <p style={{ color: 'var(--text-muted)', fontSize: '12px', fontWeight: 700, textTransform: 'uppercase', marginBottom: '8px' }}>Revenue</p>
                    <h2 style={{ color: '#059669', margin: '0 0 8px 0', fontSize: '24px', fontWeight: 900 }}>₱{stats.monthlyRevenue[expandedMonth]?.toLocaleString() || 0}</h2>
                    <span style={{ fontSize: '11px', background: 'var(--light-bg)', padding: '4px 8px', borderRadius: '8px', fontWeight: 700 }}>{stats.monthDetails[expandedMonth]?.length || 0} Paid</span>
                  </div>
                  <div>
                    <p style={{ color: 'var(--text-muted)', fontSize: '12px', fontWeight: 700, textTransform: 'uppercase', marginBottom: '8px' }}>Pending</p>
                    <h2 style={{ color: '#F59E0B', margin: '0 0 8px 0', fontSize: '24px', fontWeight: 900 }}>₱{stats.monthlyPending[expandedMonth]?.toLocaleString() || 0}</h2>
                    <span style={{ fontSize: '11px', background: 'var(--light-bg)', padding: '4px 8px', borderRadius: '8px', fontWeight: 700 }}>{stats.pendingDetails[expandedMonth]?.length || 0} Pending</span>
                  </div>
                  <div>
                    <p style={{ color: 'var(--text-muted)', fontSize: '12px', fontWeight: 700, textTransform: 'uppercase', marginBottom: '8px' }}>Expected Total</p>
                    <h2 style={{ color: 'var(--primary)', margin: '0 0 8px 0', fontSize: '24px', fontWeight: 900 }}>₱{((stats.monthlyRevenue[expandedMonth] || 0) + (stats.monthlyPending[expandedMonth] || 0)).toLocaleString()}</h2>
                  </div>
                </div>

                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', maxHeight: '50vh', overflowY: 'auto', paddingRight: '4px' }}>
                  {(stats.monthDetails[expandedMonth] || []).map((b, i) => (
                    <div key={`paid-${i}`} style={{ padding: '20px', borderRadius: '20px', background: 'var(--light-bg)', border: '1px solid var(--border)' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '16px' }}>
                        <h4 style={{ margin: 0, fontSize: '16px', fontWeight: 900 }}>{b.room}</h4>
                        <div style={{ textAlign: 'right' }}>
                          <span style={{ fontSize: '16px', fontWeight: 800, color: '#059669' }}>₱{b.amount.toLocaleString()}</span>
                          <div style={{ fontSize: '11px', color: '#059669', fontWeight: 700 }}>PAID</div>
                        </div>
                      </div>

                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px', color: 'var(--text-color)' }}>
                        <User size={16} color="var(--text-muted)" />
                        <span style={{ fontWeight: 600, fontSize: '14px' }}>{b.tourist}</span>
                      </div>

                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px', color: 'var(--text-color)' }}>
                        <Calendar size={16} color="var(--text-muted)" />
                        {(() => {
                          const isAct = b.rawBooking?.isActivityBooking === true || (
                            !b.rawBooking?.roomId &&
                            !b.rawBooking?.roomTitle &&
                            !(b.rawBooking?.activityTitle && String(b.rawBooking?.activityTitle).toLowerCase().includes('room')) &&
                            !b.rawBooking?.nights &&
                            Boolean(b.rawBooking?.activityId && String(b.rawBooking?.activityId).trim() !== '')
                          );
                          if (isAct) {
                            const hours = parseInt(b.rawBooking?.hours || b.rawBooking?.nights || b.nights || 1);
                            return <span style={{ fontSize: '14px' }}>{b.date} ({hours} {hours === 1 ? 'hour' : 'hours'})</span>;
                          }
                          return <span style={{ fontSize: '14px' }}>{b.date} ({b.nights} {b.nights === 1 ? 'night' : 'nights'})</span>;
                        })()}
                      </div>
                    </div>
                  ))}

                  {(stats.pendingDetails[expandedMonth] || []).map((b, i) => (
                    <div key={`pending-${i}`} style={{ padding: '20px', borderRadius: '20px', background: 'var(--surface)', border: '1px dashed #FCD34D' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '16px' }}>
                        <h4 style={{ margin: 0, fontSize: '16px', fontWeight: 900 }}>{b.room}</h4>
                        <div style={{ textAlign: 'right' }}>
                          <span style={{ fontSize: '16px', fontWeight: 800, color: '#F59E0B' }}>₱{b.amount.toLocaleString()}</span>
                          <div style={{ fontSize: '11px', color: '#F59E0B', fontWeight: 700 }}>PENDING BALANCE</div>
                        </div>
                      </div>

                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px', color: 'var(--text-color)' }}>
                        <User size={16} color="var(--text-muted)" />
                        <span style={{ fontWeight: 600, fontSize: '14px' }}>{b.tourist}</span>
                      </div>

                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px', color: 'var(--text-color)' }}>
                        <Calendar size={16} color="var(--text-muted)" />
                        {(() => {
                          const isAct = b.rawBooking?.isActivityBooking === true || (
                            !b.rawBooking?.roomId &&
                            !b.rawBooking?.roomTitle &&
                            !(b.rawBooking?.activityTitle && String(b.rawBooking?.activityTitle).toLowerCase().includes('room')) &&
                            !b.rawBooking?.nights &&
                            Boolean(b.rawBooking?.activityId && String(b.rawBooking?.activityId).trim() !== '')
                          );
                          if (isAct) {
                            const hours = parseInt(b.rawBooking?.hours || b.rawBooking?.nights || b.nights || 1);
                            return <span style={{ fontSize: '14px' }}>{b.date} ({hours} {hours === 1 ? 'hour' : 'hours'})</span>;
                          }
                          return <span style={{ fontSize: '14px' }}>{b.date} ({b.nights} {b.nights === 1 ? 'night' : 'nights'})</span>;
                        })()}
                      </div>
                    </div>
                  ))}
                </div>
              </>
            ) : (
              <>
                <div className="no-print" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
                  <h3 style={{ margin: 0, fontWeight: 800 }}>Sales Report</h3>
                  <div style={{ display: 'flex', gap: '8px' }}>
                    <button onClick={() => window.print()} className="btn btn-secondary" style={{ padding: '8px 16px', borderRadius: '12px', fontSize: '13px' }}>Print Report</button>
                    <button onClick={() => setShowRevenue(false)} className="close-btn"><X size={20} /></button>
                  </div>
                </div>
                <h3 className="print-only" style={{ display: 'none', margin: '0 0 20px 0', fontSize: '24px', fontWeight: 800 }}>Sales Report</h3>

                <div style={{ background: 'var(--light-bg)', padding: '24px', borderRadius: '24px', marginBottom: '32px' }}>
                  <div className="no-print" style={{ marginBottom: '20px', display: 'flex', gap: '12px' }}>
                    <div style={{ flex: 1 }}>
                      <label className="input-label" style={{ display: 'block', marginBottom: '8px', fontSize: '11px', fontWeight: 800, color: 'var(--text-muted)', textTransform: 'uppercase' }}>Filter by Year</label>
                      <select
                        className="input"
                        value={revenueYearFilter}
                        onChange={(e) => setRevenueYearFilter(e.target.value)}
                        style={{ width: '100%', background: 'var(--surface)', color: 'var(--text-main)' }}
                      >
                        {stats.availableYears.map(y => <option key={y} value={y}>{y}</option>)}
                      </select>
                    </div>
                    <div style={{ flex: 1 }}>
                      <label className="input-label" style={{ display: 'block', marginBottom: '8px', fontSize: '11px', fontWeight: 800, color: 'var(--text-muted)', textTransform: 'uppercase' }}>Filter by Month</label>
                      <select
                        className="input"
                        value={revenueFilter}
                        onChange={(e) => setRevenueFilter(e.target.value)}
                        style={{ width: '100%', background: 'var(--surface)', color: 'var(--text-main)' }}
                      >
                        {stats.availableMonths.map(m => <option key={m} value={m}>{m}</option>)}
                      </select>
                    </div>
                  </div>
                  <div style={{ textAlign: 'center' }}>
                    <p style={{ color: 'var(--text-muted)', fontSize: '13px', fontWeight: 700, textTransform: 'uppercase', marginBottom: '8px' }}>Most Booked Room</p>
                    <h2 style={{ color: 'var(--secondary)', margin: '0 0 16px 0', fontSize: '24px', fontWeight: 800 }}>{stats.bestSeller}</h2>
                    <div style={{ borderTop: '1px dashed var(--border-dashed)', paddingTop: '16px', display: 'flex', justifyContent: 'space-around', alignItems: 'center' }}>
                      <div>
                        <p style={{ color: 'var(--text-muted)', fontSize: '11px', fontWeight: 700, textTransform: 'uppercase', marginBottom: '4px' }}>Current Revenue</p>
                        <h2 style={{ color: '#059669', margin: 0, fontSize: '20px', fontWeight: 900 }}>₱{stats.totalRevenue.toLocaleString()}</h2>
                      </div>
                      <div style={{ fontSize: '20px', color: 'var(--text-muted)' }}>+</div>
                      <div>
                        <p style={{ color: 'var(--text-muted)', fontSize: '11px', fontWeight: 700, textTransform: 'uppercase', marginBottom: '4px' }}>Pending Balances</p>
                        <h2 style={{ color: '#F59E0B', margin: 0, fontSize: '20px', fontWeight: 900 }}>₱{stats.totalPending.toLocaleString()}</h2>
                      </div>
                      <div style={{ fontSize: '20px', color: 'var(--text-muted)' }}>=</div>
                      <div>
                        <p style={{ color: 'var(--text-muted)', fontSize: '11px', fontWeight: 700, textTransform: 'uppercase', marginBottom: '4px' }}>Expected Total</p>
                        <h2 style={{ color: 'var(--primary)', margin: 0, fontSize: '20px', fontWeight: 900 }}>₱{(stats.totalRevenue + stats.totalPending).toLocaleString()}</h2>
                      </div>
                    </div>
                  </div>
                </div>

                <h4 style={{ fontSize: '16px', fontWeight: 800, marginBottom: '16px' }}>Monthly Breakdown</h4>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', maxHeight: '40vh', overflowY: 'auto', paddingRight: '4px' }}>
                  {Object.entries(stats.monthlyRevenue).length > 0 ? Object.entries(stats.monthlyRevenue).map(([month, rev]) => (
                    <div
                      key={month}
                      onClick={() => setExpandedMonth(month)}
                      style={{ display: 'flex', justifyContent: 'space-between', padding: '16px', borderRadius: '16px', background: 'var(--light-bg)', border: '1px solid var(--border)', cursor: 'pointer', alignItems: 'center', transition: 'all 0.2s' }}
                      className="month-row-hover"
                    >
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <span style={{ fontWeight: 600 }}>{month}</span>
                        <span style={{ fontSize: '10px', background: 'rgba(0,0,0,0.05)', padding: '2px 6px', borderRadius: '10px' }}>
                          {stats.monthDetails[month]?.length || 0} bookings
                        </span>
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <div style={{ textAlign: 'right' }}>
                          <div style={{ fontWeight: 800, color: '#059669' }}>₱{rev.toLocaleString()}</div>
                          {stats.monthlyPending[month] > 0 && (
                            <div style={{ fontSize: '11px', color: '#F59E0B', fontWeight: 700 }}>+ ₱{stats.monthlyPending[month].toLocaleString()} Pending</div>
                          )}
                        </div>
                        <ChevronRight size={16} color="var(--text-muted)" />
                      </div>
                    </div>
                  )) : <p style={{ textAlign: 'center', color: 'var(--text-muted)', fontSize: '14px' }}>No revenue data yet.</p>}
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {showAddRoom && (
        <AddRoomModal
          uid={uid}
          rooms={rooms}
          roomToEdit={roomToEdit}
          onClose={() => { setShowAddRoom(false); setRoomToEdit(null); }}
        />
      )}

      {showAddActivity && (
        <AddActivityModal
          uid={uid}
          activities={activities}
          activityToEdit={activityToEdit}
          onClose={() => { setShowAddActivity(false); setActivityToEdit(null); }}
        />
      )}

      {showEditProperty && <EditPropertyModal uid={uid} onClose={() => setShowEditProperty(false)} />}

      {showScanner && (
        <QrScanner
          onResult={async (booking) => {
            setShowScanner(false);
            setScannedViaQr(true);
            setScannedBooking(booking);
          }}
          onClose={() => setShowScanner(false)}
        />
      )}

      {scannedBooking && (
        <div className="modal-overlay" onClick={() => setScannedBooking(null)} style={{ zIndex: 4000 }}>
          <div className="card modal-content view-transition" onClick={e => e.stopPropagation()} style={{ maxWidth: '550px', borderRadius: '32px', padding: '32px', background: 'var(--surface)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
              <h3 style={{ margin: 0, fontWeight: 800, fontSize: '24px' }}>Verification Result</h3>
              <button onClick={() => setScannedBooking(null)} className="close-btn"><X size={20} /></button>
            </div>

            <div style={{ background: 'var(--light-bg)', padding: '24px', borderRadius: '24px', border: '1px solid var(--border)' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '24px', paddingBottom: '20px', borderBottom: '1px solid #E5E7EB' }}>
                <div style={{ width: '60px', height: '60px', borderRadius: '18px', background: 'var(--surface)', display: 'flex', justifyContent: 'center', alignItems: 'center', boxShadow: '0 4px 12px rgba(0,0,0,0.05)', overflow: 'hidden' }}>
                  {scannedTouristPhoto ? (
                    <img src={scannedTouristPhoto} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                  ) : (
                    <User size={32} color="var(--secondary)" />
                  )}
                </div>
                <div>
                  <h4 style={{ margin: 0, fontSize: '18px', fontWeight: 800 }}>{scannedTouristName || scannedBooking.touristName || 'Guest'}</h4>
                  <p style={{ margin: '2px 0 0 0', fontSize: '13px', color: 'var(--text-muted)', fontWeight: 600 }}>Guest Verification</p>
                </div>
              </div>

              <div style={{ display: 'grid', gap: '16px' }}>
                {(() => {
                  const isAct = scannedBooking.isActivityBooking === true || (
                    !scannedBooking.roomId &&
                    !scannedBooking.roomTitle &&
                    !(scannedBooking.activityTitle && String(scannedBooking.activityTitle).toLowerCase().includes('room')) &&
                    !scannedBooking.nights &&
                    Boolean(scannedBooking.activityId && String(scannedBooking.activityId).trim() !== '')
                  );
                  const duration = parseInt(scannedBooking.hours || scannedBooking.nights || 1);
                  return (
                    <>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                        <HomeIcon size={18} color="var(--primary)" />
                        <div>
                          <p style={{ margin: 0, fontSize: '11px', fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase' }}>{isAct ? 'Booked Activity' : 'Booked Room'}</p>
                          <p style={{ margin: 0, fontSize: '15px', fontWeight: 800 }}>{scannedBooking.activityTitle || scannedBooking.roomTitle}</p>
                        </div>
                      </div>

                      {scannedBooking.isHistorical === true ? (
                        <>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                            <Calendar size={18} color="var(--secondary)" />
                            <div>
                              <p style={{ margin: 0, fontSize: '11px', fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase' }}>Arrival Date</p>
                              <p style={{ margin: 0, fontSize: '15px', fontWeight: 800 }}>
                                {(() => {
                                  const pStart = parseDateSafely(scannedBooking.bookingDate || scannedBooking.checkInDate || scannedBooking.date);
                                  return pStart ? format(pStart, 'MMM dd, yyyy') : (scannedBooking.bookingDate || 'N/A');
                                })()}
                              </p>
                            </div>
                          </div>
                          {!isAct && (
                            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                              <Calendar size={18} color="var(--primary)" />
                              <div>
                                <p style={{ margin: 0, fontSize: '11px', fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase' }}>Departure Date</p>
                                <p style={{ margin: 0, fontSize: '15px', fontWeight: 800 }}>
                                  {(() => {
                                    const pStart = parseDateSafely(scannedBooking.bookingDate || scannedBooking.checkInDate || scannedBooking.date);
                                    const pDep = parseDateSafely(scannedBooking.departureDate || scannedBooking.checkOutDate) || (pStart ? addDays(pStart, duration) : null);
                                    return pDep ? format(pDep, 'MMM dd, yyyy') : (scannedBooking.departureDate || 'N/A');
                                  })()}
                                  <span style={{ color: 'var(--text-muted)', fontWeight: 600, fontSize: '13px', marginLeft: '8px' }}>
                                    ({duration} {duration === 1 ? 'Night' : 'Nights'})
                                  </span>
                                </p>
                              </div>
                            </div>
                          )}
                        </>
                      ) : (
                        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                          <Calendar size={18} color="var(--secondary)" />
                          <div>
                            <p style={{ margin: 0, fontSize: '11px', fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase' }}>{isAct ? 'Activity Date & Duration' : 'Check-in Date & Duration'}</p>
                            <p style={{ margin: 0, fontSize: '15px', fontWeight: 800 }}>
                              {formatBookingDateRange(scannedBooking)}
                              <span style={{ color: 'var(--text-muted)', fontWeight: 600, fontSize: '13px', marginLeft: '8px' }}>
                                ({duration} {isAct ? 'Hour/s' : (duration === 1 ? 'Night' : 'Nights')})
                              </span>
                            </p>
                          </div>
                        </div>
                      )}

                      <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                        <Clock size={18} color="var(--primary)" />
                        <div>
                          <p style={{ margin: 0, fontSize: '11px', fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase' }}>Guest Arrival Time</p>
                          <p style={{ margin: 0, fontSize: '14px', fontWeight: 800, color: 'var(--text-main)' }}>
                            {scannedBooking.arrivalTime || scannedBooking.timeSlot || scannedBooking.bookingTime || (isAct ? 'Regular Operating Hours' : 'Check-in Time')}
                          </p>
                        </div>
                      </div>
                    </>
                  );
                })()}

                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                  <TrendingUp size={18} color="#10B981" />
                  <div style={{ flex: 1 }}>
                    <p style={{ margin: 0, fontSize: '11px', fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase' }}>Payment Breakdown</p>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '4px' }}>
                      <span style={{ fontSize: '13px', fontWeight: 600 }}>Total:</span>
                      <span style={{ fontSize: '13px', fontWeight: 700 }}>₱{scannedBooking.totalPrice?.toLocaleString()}</span>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                      <span style={{ fontSize: '13px', fontWeight: 600, color: '#059669' }}>Paid:</span>
                      <span style={{ fontSize: '13px', fontWeight: 800, color: '#059669' }}>₱{(scannedBooking.amountPaid || (scannedBooking.paymentOption?.includes('30%') ? scannedBooking.totalPrice * 0.3 : scannedBooking.totalPrice))?.toLocaleString()}</span>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', borderTop: '1px dashed #E5E7EB', marginTop: '4px', paddingTop: '4px' }}>
                      <span style={{ fontSize: '13px', fontWeight: 700 }}>Balance:</span>
                      <span style={{ fontSize: '15px', fontWeight: 900, color: 'var(--primary)' }}>
                        ₱{(scannedBooking.totalPrice - (scannedBooking.amountPaid || (scannedBooking.paymentOption?.includes('30%') ? scannedBooking.totalPrice * 0.3 : scannedBooking.totalPrice)))?.toLocaleString()}
                      </span>
                    </div>
                  </div>
                </div>

                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                  <CreditCard size={18} color="#1D4ED8" />
                  <div>
                    <p style={{ margin: 0, fontSize: '11px', fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase' }}>Payment Details</p>
                    <p style={{ margin: 0, fontSize: '14px', fontWeight: 700 }}>{scannedBooking.paymentOption || 'Full Payment'} via {scannedBooking.paymentMethod || 'GCash'}</p>
                  </div>
                </div>

                {scannedBooking.status === 'Refund Requested' && (
                  <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px', background: 'rgba(239, 68, 68, 0.05)', padding: '12px', borderRadius: '12px' }}>
                    <AlertCircle size={18} color="#EF4444" style={{ marginTop: '2px' }} />
                    <div>
                      <p style={{ margin: 0, fontSize: '11px', fontWeight: 700, color: '#EF4444', textTransform: 'uppercase' }}>Refund Request Details</p>
                      <p style={{ margin: '4px 0 0 0', fontSize: '13px', fontWeight: 600 }}>Reason: {scannedBooking.refundReason || 'No reason provided'}</p>
                      <p style={{ margin: '2px 0 0 0', fontSize: '13px', fontWeight: 800 }}>Send To: {scannedBooking.touristGcashName || scannedTouristGcashName || scannedBooking.gcashName || 'N/A'} ({scannedBooking.touristGcashNumber || scannedTouristGcashNumber || scannedBooking.gcashNumber || 'N/A'})</p>
                    </div>
                  </div>
                )}
                {scannedBooking.status === 'Reschedule Requested' && (
                  <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px', background: 'rgba(79, 70, 229, 0.05)', padding: '12px', borderRadius: '12px', marginTop: '12px' }}>
                    <AlertCircle size={18} color="#4F46E5" style={{ marginTop: '2px' }} />
                    <div>
                      <p style={{ margin: 0, fontSize: '11px', fontWeight: 700, color: '#4F46E5', textTransform: 'uppercase' }}>Reschedule Reason</p>
                      <p style={{ margin: '4px 0 0 0', fontSize: '13px', fontWeight: 600 }}>{scannedBooking.rescheduleReason || 'None provided'}</p>
                    </div>
                  </div>
                )}

                {scannedBooking.selectedAddons && scannedBooking.selectedAddons.length > 0 && (
                  <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
                    <PlusSquare size={18} color="var(--primary)" style={{ marginTop: '2px' }} />
                    <div>
                      <p style={{ margin: 0, fontSize: '11px', fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase' }}>Add-ons</p>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginTop: '4px' }}>
                        {scannedBooking.selectedAddons.map((a, i) => (
                          <span key={i} style={{ background: 'var(--surface)', padding: '4px 10px', borderRadius: '8px', fontSize: '12px', fontWeight: 700, border: '1px solid var(--border)' }}>{a}</span>
                        ))}
                      </div>
                    </div>
                  </div>
                )}

                <div style={{ marginTop: '16px', display: 'flex', justifyContent: 'center' }}>
                  <button className="btn" style={{ background: 'rgba(79, 70, 229, 0.1)', color: '#4F46E5', fontSize: '13px', fontWeight: 800, border: '1px solid rgba(79, 70, 229, 0.2)', width: '100%', display: 'flex', gap: '8px', alignItems: 'center', justifyContent: 'center' }} onClick={() => setShowBreakdownBooking(scannedBooking)}>
                    <ShoppingBag size={16} /> View Price Breakdown
                  </button>
                </div>
              </div>
            </div>

            <div style={{ display: 'flex', gap: '12px', marginTop: '32px' }}>
              <button className="btn" style={{ flex: 1, background: 'var(--surface)', color: 'var(--text-main)', border: '1px solid var(--border)', fontSize: '13px' }} onClick={() => setScannedBooking(null)}>Close</button>
              {scannedBooking.gcashReceipt && scannedBooking.gcashReceipt !== 'MANUAL_GCASH_PAYMENT' && (
                <button
                  type="button"
                  onClick={() => setPreviewReceipt(scannedBooking.gcashReceipt)}
                  className="btn"
                  style={{ background: 'rgba(29, 78, 216, 0.1)', color: '#3B82F6', padding: '10px 16px', fontSize: '13px', display: 'flex', alignItems: 'center', gap: '6px', border: 'none', cursor: 'pointer' }}
                >
                  <Eye size={16} /> View Receipt
                </button>
              )}
            </div>

            {(scannedBooking.ocrStatus || scannedBooking.extractedRefNo) && (() => {
              const ocrStatus = scannedBooking.ocrStatus || 'Unverified';
              const isVerified = ocrStatus === 'Verified';
              const rawReason = scannedBooking.ocrIssues || scannedBooking.ocrReason || scannedBooking.flaggedReason || scannedBooking.verificationDetails;
              let reasonText = '';
              if (typeof rawReason === 'string') {
                reasonText = rawReason.trim();
              } else if (Array.isArray(rawReason)) {
                reasonText = rawReason.filter(Boolean).join(', ').trim();
              } else if (rawReason && typeof rawReason === 'object') {
                reasonText = Object.values(rawReason).filter(Boolean).join(', ').trim();
              }
              if (!reasonText && !isVerified) {
                reasonText = 'No detailed reason was recorded for this booking.';
              }

              return (
                <div style={{ marginTop: '12px', padding: '12px', background: isVerified ? 'rgba(16, 185, 129, 0.1)' : 'rgba(245, 158, 11, 0.1)', border: `1px solid ${isVerified ? 'rgba(16, 185, 129, 0.2)' : 'rgba(245, 158, 11, 0.2)'}`, borderRadius: '12px', display: 'flex', alignItems: 'flex-start', gap: '8px' }}>
                  {isVerified ? <CheckCircle2 size={16} color="#059669" style={{marginTop: '2px'}}/> : <AlertCircle size={16} color="#B45309" style={{marginTop: '2px'}}/>}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <span style={{ fontSize: '13px', fontWeight: 800, color: isVerified ? '#059669' : '#B45309', display: 'block' }}>
                      AI Verification: {isVerified ? 'Passed' : (ocrStatus === 'Multiple Receipts (Manual Review)' ? 'Multiple Receipts' : 'Flagged for Review')}
                    </span>
                    {scannedBooking.extractedRefNo && (
                      <span style={{ fontSize: '12px', color: isVerified ? '#059669' : '#B45309', display: 'block', marginTop: '2px', fontWeight: 600 }}>
                        Ref No: {scannedBooking.extractedRefNo}
                      </span>
                    )}
                    {reasonText && (
                      <span style={{ fontSize: '11px', color: '#B45309', display: 'block', marginTop: '4px', fontWeight: 600, wordBreak: 'break-word' }}>
                        Reason: {reasonText}
                      </span>
                    )}
                  </div>
                </div>
              );
            })()}

            {['Pending', 'Reschedule Requested', 'Refund Requested', 'Confirmed', 'Checked In', 'Refund Declined', 'No Show'].includes(scannedBooking.status || 'Pending') && (
              <div style={{ display: 'flex', gap: '10px', marginTop: '12px' }}>
                {(scannedBooking.status || 'Pending').toLowerCase() === 'pending' && (
                  <>
                    <button className="btn" style={{ background: 'rgba(251, 54, 64, 0.15)', color: 'var(--primary)', flex: 1, fontSize: '13px' }} onClick={() => { initiateUpdateStatus(scannedBooking.id, 'Cancelled'); }}>Decline</button>
                    <button className="btn btn-primary" style={{ flex: 1.5, fontSize: '13px' }} onClick={() => { initiateUpdateStatus(scannedBooking.id, 'Confirmed'); }}>Confirm</button>
                  </>
                )}
                {scannedBooking.status === 'Reschedule Requested' && (
                  <>
                    <button className="btn" style={{ background: 'rgba(251, 54, 64, 0.15)', color: 'var(--primary)', flex: 1, fontSize: '13px' }} onClick={() => { initiateUpdateStatus(scannedBooking.id, 'Reschedule Declined'); }}>Decline Reschedule</button>
                    <button className="btn btn-primary" style={{ flex: 1.5, fontSize: '13px', background: '#059669' }} onClick={() => { initiateUpdateStatus(scannedBooking.id, 'Reschedule Approved'); }}>Approve</button>
                  </>
                )}
                {scannedBooking.status === 'Refund Requested' && (
                  <>
                    <button className="btn" style={{ background: 'rgba(251, 54, 64, 0.15)', color: 'var(--primary)', flex: 1, fontSize: '13px' }} onClick={() => { initiateUpdateStatus(scannedBooking.id, 'Refund Declined'); }}>Decline Refund</button>
                    <button className="btn btn-primary" style={{ flex: 1.5, fontSize: '13px', background: '#059669' }} onClick={() => { initiateUpdateStatus(scannedBooking.id, 'Refund Approved'); }}>Approve</button>
                  </>
                )}
                {scannedBooking.status === 'Refund Approved' && (
                  <button className="btn btn-primary" style={{ width: '100%', fontSize: '13px', background: '#059669' }} onClick={() => { initiateUpdateStatus(scannedBooking.id, 'Refund Confirmed'); }}>Confirm Refund</button>
                )}
                {((scannedBooking.status || '').toLowerCase() === 'confirmed' || (scannedBooking.status || '').toLowerCase() === 'refund declined') && (
                  <>
                    {(() => {
                      const today = new Date();
                      today.setHours(0, 0, 0, 0);
                      let canCheckIn = true;
                      let isExpired = false;
                      const dateStr = scannedBooking.bookingDate || scannedBooking.checkInDate || scannedBooking.date;
                      try {
                        if (dateStr) {
                          const bDate = parseDateSafely(dateStr);
                          if (bDate) {
                            bDate.setHours(0, 0, 0, 0);
                            const nights = parseInt(scannedBooking.nights) || 1;
                            const endDate = addDays(bDate, nights);
                            endDate.setHours(0, 0, 0, 0);
                            
                            if (today > endDate) {
                              canCheckIn = false;
                              isExpired = true;
                            } else {
                              canCheckIn = today >= bDate;
                            }
                          }
                        }
                      } catch(e) {}

                      if (isExpired) {
                        return (
                          <button 
                            className="btn" 
                            style={{ background: '#DC2626', color: 'white', width: '100%', fontSize: '13px', cursor: 'pointer' }} 
                            onClick={() => { initiateUpdateStatus(scannedBooking.id, 'No Show'); }}
                          >
                            Mark as No Show (Checkout Expired)
                          </button>
                        );
                      }

                      if (scannedViaQr) {
                        return (
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', width: '100%' }}>
                            <button 
                              className="btn" 
                              style={{ background: canCheckIn ? '#4F46E5' : '#9CA3AF', color: 'white', width: '100%', fontSize: '13px', cursor: canCheckIn ? 'pointer' : 'not-allowed' }} 
                              disabled={!canCheckIn}
                              onClick={() => { initiateUpdateStatus(scannedBooking.id, 'Checked In'); }}
                            >
                              {canCheckIn ? 'Check In Customer' : `Check-in on ${dateStr || 'Date'}`}
                            </button>
                            <button
                              className="btn"
                              style={{ background: 'rgba(239, 68, 68, 0.1)', color: '#DC2626', border: '1px solid rgba(220, 38, 38, 0.25)', width: '100%', fontSize: '13px', cursor: 'pointer' }}
                              onClick={() => {
                                if (window.confirm("Are you sure you want to mark this guest as No Show?")) {
                                  initiateUpdateStatus(scannedBooking.id, 'No Show', 'Guest did not show up.');
                                }
                              }}
                            >
                              Mark as No Show
                            </button>
                          </div>
                        );
                      }

                      return (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', width: '100%' }}>
                          <div style={{ textAlign: 'center', width: '100%', color: 'var(--primary)', fontSize: '12px', fontWeight: 700, padding: '10px', background: 'rgba(251, 54, 64, 0.1)', borderRadius: '12px' }}>
                            <AlertCircle size={14} style={{ marginBottom: '-2px', marginRight: '4px' }} />
                            Please use the QR Scanner to Check-In the guest.
                          </div>
                          <button
                            className="btn"
                            style={{ background: 'rgba(239, 68, 68, 0.1)', color: '#DC2626', border: '1px solid rgba(220, 38, 38, 0.25)', width: '100%', fontSize: '13px', cursor: 'pointer' }}
                            onClick={() => {
                              if (window.confirm("Are you sure you want to mark this guest as No Show?")) {
                                initiateUpdateStatus(scannedBooking.id, 'No Show', 'Guest did not show up.');
                              }
                            }}
                          >
                            Mark as No Show
                          </button>
                        </div>
                      );
                    })()}
                  </>
                )}
                {(scannedBooking.status || '').toLowerCase() === 'checked in' && (
                  <>
                    <button className="btn" style={{ background: 'var(--secondary)', color: '#002D24', width: '100%', fontSize: '13px' }} onClick={() => { initiateUpdateStatus(scannedBooking.id, 'Completed'); }}>VERIFY CHECK-OUT</button>
                    <button className="btn" style={{ background: '#3B82F6', color: 'white', width: '100%', fontSize: '13px', marginTop: '8px' }} onClick={() => { setExtendStayConfig({ isOpen: true, bookingId: scannedBooking.id, nights: 1, isLoading: false, error: '' }); }}>EXTEND STAY</button>
                  </>
                )}
                {(scannedBooking.status || '').toLowerCase() === 'no show' && (
                  <button 
                    className="btn" 
                    style={{ background: '#FEE2E2', border: '1px solid #FECACA', color: '#EF4444', width: '100%', fontSize: '13px' }} 
                    onClick={() => { deleteBooking(scannedBooking.id); setScannedBooking(null); }}
                  >
                    Delete No Show Record
                  </button>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {showBreakdownBooking && (
        <div className="modal-overlay" onClick={() => setShowBreakdownBooking(null)} style={{ zIndex: 6000 }}>
          <div className="card modal-content" style={{ maxWidth: '450px', background: 'var(--surface)', borderRadius: '24px' }} onClick={e => e.stopPropagation()}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
              <h3 style={{ margin: 0, fontWeight: 800, fontSize: '20px' }}>Price Breakdown</h3>
              <button onClick={() => setShowBreakdownBooking(null)} className="close-btn"><X size={18} /></button>
            </div>

            {(() => {
              const isOldBooking = !showBreakdownBooking.pricing && showBreakdownBooking.selectedAddons?.length > 0;
              let calculatedAddonsTotal = showBreakdownBooking.pricing?.addonsTotal || 0;

              if (isOldBooking) {
                showBreakdownBooking.selectedAddons.forEach(addonStr => {
                  try {
                    const match = addonStr.match(/(.+?)\s*\(x(\d+)\)/);
                    if (match && Object.keys(breakdownAddonPrices).length > 0) {
                      const name = match[1].trim();
                      const qty = parseInt(match[2]);
                      const pricePerUnit = breakdownAddonPrices[name] || 0;
                      calculatedAddonsTotal += pricePerUnit * qty;
                    }
                  } catch (e) { }
                });
              }

              const grandTotal = Number(showBreakdownBooking.totalPrice ?? showBreakdownBooking.pricing?.grandTotal ?? 0);

              // Read promo discount in specified fallback order
              const rawDiscount = showBreakdownBooking.promoDiscount ??
                showBreakdownBooking.pricing?.promoDiscount ??
                showBreakdownBooking.pricing?.discount ??
                showBreakdownBooking.discountAmount;
              let discountAmount = Math.abs(Number(rawDiscount || 0));
              if (isNaN(discountAmount)) discountAmount = 0;

              const rawCode = (showBreakdownBooking.promoCode || '').trim();
              const rawName = (showBreakdownBooking.promoName || '').trim();
              let discountLabel = "Promo Discount";
              if (rawCode) {
                discountLabel = `Promo Discount (${rawCode})`;
              } else if (rawName) {
                discountLabel = `Promo Discount (${rawName})`;
              }

              const taxes = Number(showBreakdownBooking.pricing?.taxes ?? showBreakdownBooking.pricing?.taxesAndFees ?? 0);

              let basePrice = showBreakdownBooking.pricing?.basePrice;
              if (basePrice === undefined || basePrice === null) {
                basePrice = Math.max(0, grandTotal - calculatedAddonsTotal + discountAmount - taxes);
              } else {
                basePrice = Number(basePrice);
              }

              const isAct = showBreakdownBooking.isActivityBooking === true || (
                !showBreakdownBooking.roomId &&
                !showBreakdownBooking.roomTitle &&
                !(showBreakdownBooking.activityTitle && String(showBreakdownBooking.activityTitle).toLowerCase().includes('room')) &&
                !showBreakdownBooking.nights &&
                Boolean(showBreakdownBooking.activityId && String(showBreakdownBooking.activityId).trim() !== '')
              );
              const duration = parseInt(showBreakdownBooking.hours || showBreakdownBooking.nights || 1);
              const baseLabel = isAct ? `Activity Base (${duration} Hour/s)` : `Room Base (${showBreakdownBooking.nights || 1} Night/s)`;

              return (
                <div style={{ background: 'var(--light-bg)', padding: '20px', borderRadius: '16px', display: 'flex', flexDirection: 'column', gap: '12px', border: '1px solid var(--border)' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontWeight: 700 }}>
                    <span style={{ color: 'var(--text-main)' }}>{baseLabel}</span>
                    <span style={{ color: 'var(--text-main)' }}>₱{basePrice.toLocaleString()}</span>
                  </div>

                  {(calculatedAddonsTotal > 0 || showBreakdownBooking.selectedAddons?.length > 0) && (
                    <>
                      <div style={{ height: '1px', background: 'var(--border)', margin: '8px 0' }} />
                      <div style={{ fontWeight: 800, fontSize: '12px', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>ADD-ONS</div>

                      {showBreakdownBooking.pricing?.addonsList?.length > 0 ? (
                        showBreakdownBooking.pricing.addonsList.map((addon, idx) => (
                          <div key={idx} style={{ display: 'flex', justifyContent: 'space-between', fontSize: '14px', color: 'var(--text-muted)' }}>
                            <span>{addon.name}: ₱{(addon.total / addon.quantity).toLocaleString()} (x{addon.quantity})</span>
                            <span style={{ fontWeight: 600 }}>₱{addon.total.toLocaleString()}</span>
                          </div>
                        ))
                      ) : (
                        showBreakdownBooking.selectedAddons?.map((addonStr, idx) => {
                          let displayPrice = "Included in subtotal";
                          let displayName = addonStr;
                          try {
                            const match = addonStr.match(/(.+?)\s*\(x(\d+)\)/);
                            if (match && Object.keys(breakdownAddonPrices).length > 0) {
                              const name = match[1].trim();
                              const qty = parseInt(match[2]);
                              const pricePerUnit = breakdownAddonPrices[name] || 0;
                              if (pricePerUnit > 0) {
                                displayName = `${name}: ₱${pricePerUnit.toLocaleString()} (x${qty})`;
                                displayPrice = `₱${(pricePerUnit * qty).toLocaleString()}`;
                              }
                            }
                          } catch (e) { }
                          return (
                            <div key={idx} style={{ display: 'flex', justifyContent: 'space-between', fontSize: '14px', color: 'var(--text-muted)' }}>
                              <span>{displayName}</span>
                              <span style={{ fontWeight: 600 }}>{displayPrice}</span>
                            </div>
                          );
                        })
                      )}

                      <div style={{ display: 'flex', justifyContent: 'space-between', fontWeight: 700, marginTop: '8px' }}>
                        <span style={{ color: 'var(--text-main)' }}>Add-ons Subtotal</span>
                        <span style={{ color: 'var(--text-main)' }}>₱{calculatedAddonsTotal.toLocaleString()}</span>
                      </div>
                    </>
                  )}

                  {discountAmount > 0 && (
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontWeight: 700, color: '#10B981', marginTop: '4px' }}>
                      <span>{discountLabel}</span>
                      <span>-₱{discountAmount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                    </div>
                  )}

                  {taxes > 0 && (
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontWeight: 700, color: 'var(--text-muted)', marginTop: '4px' }}>
                      <span>Taxes & Fees</span>
                      <span>₱{taxes.toLocaleString()}</span>
                    </div>
                  )}

                  <div style={{ height: '2px', background: 'var(--border)', margin: '12px 0' }} />
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span style={{ fontWeight: 800, fontSize: '16px' }}>Grand Total</span>
                    <span style={{ fontWeight: 900, fontSize: '24px', color: 'var(--primary)' }}>₱{grandTotal.toLocaleString()}</span>
                  </div>

                  {(showBreakdownBooking.amountPaid && showBreakdownBooking.amountPaid < grandTotal) && (
                    <>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '12px' }}>
                        <span style={{ fontWeight: 600, fontSize: '14px', color: 'var(--text-muted)' }}>Amount Paid (Downpayment)</span>
                        <span style={{ fontWeight: 700, fontSize: '14px', color: 'var(--text-main)' }}>₱{showBreakdownBooking.amountPaid.toLocaleString()}</span>
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '8px' }}>
                        <span style={{ fontWeight: 800, fontSize: '15px', color: 'var(--text-main)' }}>Outstanding Balance</span>
                        <span style={{ fontWeight: 900, fontSize: '18px', color: '#ff9800' }}>₱{(grandTotal - showBreakdownBooking.amountPaid).toLocaleString()}</span>
                      </div>
                      <div style={{ fontSize: '12px', color: 'var(--text-muted)', fontStyle: 'italic', marginTop: '4px', textAlign: 'right' }}>
                        *To be paid upon check-in
                      </div>
                    </>
                  )}
                </div>
              );
            })()}
            <button className="btn btn-primary" style={{ width: '100%', marginTop: '24px', padding: '14px', fontSize: '15px' }} onClick={() => setShowBreakdownBooking(null)}>Close</button>
          </div>
        </div>
      )}

      {previewRoom && (
        <BookingModal
          room={previewRoom}
          property={{ id: uid, ...profile }}
          user={profile}
          onClose={() => setPreviewRoom(null)}
          isPreview={true}
        />
      )}

      {extendStayConfig.isOpen && (
        <div className="modal-overlay" onClick={() => !extendStayConfig.isLoading && setExtendStayConfig(prev => ({ ...prev, isOpen: false }))} style={{ zIndex: 5000 }}>
          <div className="card modal-content view-transition" onClick={e => e.stopPropagation()} style={{ maxWidth: '400px', borderRadius: '24px', padding: '24px', textAlign: 'center' }}>
            <h3 style={{ margin: '0 0 12px 0', fontWeight: 800, fontSize: '20px' }}>Extend Stay</h3>
            <p style={{ margin: '0 0 24px 0', fontSize: '14px', color: 'var(--text-muted)' }}>How many additional nights would the tourist like to extend?</p>
            
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '20px', marginBottom: '24px' }}>
              <button 
                className="btn" 
                style={{ borderRadius: '50%', width: '40px', height: '40px', padding: '0', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '20px' }}
                onClick={() => setExtendStayConfig(prev => ({ ...prev, nights: Math.max(1, prev.nights - 1) }))}
                disabled={extendStayConfig.nights <= 1 || extendStayConfig.isLoading}
              >
                -
              </button>
              <span style={{ fontSize: '24px', fontWeight: 800 }}>{extendStayConfig.nights}</span>
              <button 
                className="btn" 
                style={{ borderRadius: '50%', width: '40px', height: '40px', padding: '0', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '20px' }}
                onClick={() => setExtendStayConfig(prev => ({ ...prev, nights: Math.min(10, prev.nights + 1) }))}
                disabled={extendStayConfig.nights >= 10 || extendStayConfig.isLoading}
              >
                +
              </button>
            </div>

            {extendStayConfig.error && (
              <div style={{ color: '#EF4444', fontSize: '13px', fontWeight: 700, marginBottom: '16px' }}>
                {extendStayConfig.error}
              </div>
            )}

            <div style={{ display: 'flex', gap: '12px' }}>
              <button
                className="btn"
                style={{ flex: 1, background: 'var(--light-bg)', color: 'var(--text-main)', border: '1px solid var(--border)' }}
                onClick={() => setExtendStayConfig(prev => ({ ...prev, isOpen: false }))}
                disabled={extendStayConfig.isLoading}
              >
                Cancel
              </button>
              <button
                className="btn btn-primary"
                style={{ flex: 1, background: '#3B82F6' }}
                onClick={handleExtendStay}
                disabled={extendStayConfig.isLoading}
              >
                {extendStayConfig.isLoading ? 'Processing...' : 'Extend Stay'}
              </button>
            </div>
          </div>
        </div>
      )}

      {confirmAction.isOpen && (
        <div className="modal-overlay" onClick={() => setConfirmAction({ ...confirmAction, isOpen: false })} style={{ zIndex: 5000 }}>
          <div className="card modal-content view-transition" onClick={e => e.stopPropagation()} style={{ maxWidth: '400px', borderRadius: '24px', padding: '24px', textAlign: 'center' }}>
            <div style={{ background: 'rgba(29, 211, 176, 0.15)', width: '64px', height: '64px', borderRadius: '50%', display: 'flex', justifyContent: 'center', alignItems: 'center', margin: '0 auto 20px auto' }}>
              <AlertCircle size={32} color="var(--secondary)" />
            </div>
            <h3 style={{ margin: '0 0 12px 0', fontWeight: 800, fontSize: '20px' }}>Confirm Action</h3>
            <p style={{ margin: '0 0 24px 0', fontSize: '14px', color: 'var(--text-muted)' }}>{confirmAction.message}</p>

            {confirmAction.requireReason && (
              <textarea
                className="input"
                placeholder="Enter reason here..."
                value={confirmAction.reason}
                onChange={e => setConfirmAction({ ...confirmAction, reason: e.target.value })}
                style={{ width: '100%', minHeight: '80px', marginBottom: '24px', borderRadius: '12px', resize: 'none' }}
              />
            )}

            {confirmAction.hasBalance && confirmAction.newStatus === 'Checked In' && (
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', marginBottom: '24px', gap: '8px' }}>
                <div style={{ color: '#EF4444', fontWeight: 700, fontSize: '15px' }}>
                  Remaining Balance: ₱{confirmAction.balanceAmount.toFixed(2)}
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <input
                    type="checkbox"
                    checked={confirmAction.markAsPaid}
                    onChange={e => setConfirmAction({ ...confirmAction, markAsPaid: e.target.checked })}
                    id="markAsPaidCb"
                    style={{ width: '16px', height: '16px', accentColor: '#10B981', cursor: 'pointer' }}
                  />
                  <label htmlFor="markAsPaidCb" style={{ fontSize: '14px', color: 'var(--text-main)', cursor: 'pointer' }}>Mark balance as paid</label>
                </div>
              </div>
            )}

            <div style={{ display: 'flex', gap: '12px' }}>
              <button
                className="btn"
                style={{ flex: 1, background: 'var(--light-bg)', color: 'var(--text-main)', border: '1px solid var(--border)' }}
                onClick={() => setConfirmAction({ ...confirmAction, isOpen: false })}
              >
                Cancel
              </button>
              <button
                className="btn btn-primary"
                style={{ flex: 1, background: confirmAction.isDestructive ? '#EF4444' : '#10B981' }}
                onClick={handleConfirmActionSubmit}
                disabled={confirmAction.hasBalance && confirmAction.newStatus === 'Checked In' && !confirmAction.markAsPaid}
              >
                Confirm
              </button>
            </div>
          </div>
        </div>
      )}

      {roomToDisable && (
        <div className="modal-overlay" onClick={() => setRoomToDisable(null)} style={{ zIndex: 5000 }}>
          <div className="card modal-content view-transition" onClick={e => e.stopPropagation()} style={{ maxWidth: '400px', borderRadius: '24px', padding: '32px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
              <h3 style={{ margin: 0, fontWeight: 800, fontSize: '20px' }}>Disable Room</h3>
              <button onClick={() => setRoomToDisable(null)} className="close-btn"><X size={18} /></button>
            </div>
            
            <p style={{ fontSize: '14px', color: 'var(--text-muted)', marginBottom: '20px' }}>
              You are about to disable <strong>{roomToDisable.title}</strong> (e.g., for renovations). Booked dates are disabled.
            </p>

            <div style={{ marginBottom: '24px', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
              <label className="input-label" style={{ display: 'block', fontSize: '11px', fontWeight: 800, color: 'var(--text-muted)', marginBottom: '8px', textTransform: 'uppercase', width: '100%', textAlign: 'left' }}>Select Date Range</label>
              <div className="custom-datepicker-wrapper">
                <DatePicker
                  selectsRange={true}
                  startDate={disableStartDate}
                  endDate={disableEndDate}
                  onChange={(update) => {
                    setDisableStartDate(update[0]);
                    setDisableEndDate(update[1]);
                  }}
                  minDate={new Date()}
                  filterDate={(date) => !blockedDates.includes(new Date(date.getFullYear(), date.getMonth(), date.getDate()).getTime())}
                  inline
                  monthsShown={1}
                />
              </div>
            </div>

            <button 
              className="btn btn-primary" 
              style={{ width: '100%', padding: '14px', borderRadius: '12px', fontSize: '14px' }}
              onClick={confirmDisableRoom}
            >
              Confirm Disabling
            </button>
          </div>
        </div>
      )}

      {confirmPaymentAction.isOpen && (
        <div className="modal-overlay" onClick={() => setConfirmPaymentAction({ isOpen: false, type: '', payload: null, message: '' })} style={{ zIndex: 6000 }}>
          <div className="card modal-content view-transition" onClick={e => e.stopPropagation()} style={{ maxWidth: '420px', borderRadius: '32px', padding: '32px', textAlign: 'center' }}>
            <div style={{ width: '64px', height: '64px', borderRadius: '50%', background: 'rgba(16, 185, 129, 0.1)', color: '#10B981', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px' }}>
              <CheckCircle2 size={32} />
            </div>
            <h3 style={{ margin: '0 0 12px 0', fontSize: '22px', fontWeight: 800 }}>Confirm Payment</h3>
            <p style={{ marginBottom: '28px', color: 'var(--text-muted)', fontSize: '15px', lineHeight: 1.5 }}>
              {confirmPaymentAction.message}
            </p>
            <div style={{ display: 'flex', gap: '12px' }}>
              <button 
                className="btn btn-secondary" 
                onClick={() => setConfirmPaymentAction({ isOpen: false, type: '', payload: null, message: '' })}
                style={{ flex: 1, padding: '14px', borderRadius: '16px' }}
              >
                Cancel
              </button>
              <button 
                className="btn" 
                onClick={handleConfirmPayment}
                style={{ flex: 1, padding: '14px', borderRadius: '16px', background: 'linear-gradient(135deg, #10B981, #059669)', color: 'white' }}
              >
                Confirm Paid
              </button>
            </div>
          </div>
        </div>
      )}

      {selectedTouristUid && (
        <TouristProfileModal
          touristUid={selectedTouristUid}
          onClose={() => setSelectedTouristUid(null)}
        />
      )}

      {previewReceipt && (
        <div
          className="modal-overlay"
          onClick={() => setPreviewReceipt(null)}
          style={{ zIndex: 9999, background: 'rgba(0, 0, 0, 0.8)', backdropFilter: 'blur(6px)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px' }}
        >
          <div
            className="modal-content"
            onClick={e => e.stopPropagation()}
            style={{
              background: 'var(--surface)',
              borderRadius: '20px',
              maxWidth: '560px',
              width: '100%',
              padding: '24px',
              position: 'relative',
              boxShadow: '0 25px 60px rgba(0,0,0,0.4)',
              textAlign: 'center'
            }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px', paddingBottom: '12px', borderBottom: '1px solid var(--border)' }}>
              <h4 style={{ margin: 0, fontSize: '18px', fontWeight: 800 }}>GCash Payment Receipt</h4>
              <button
                type="button"
                onClick={() => setPreviewReceipt(null)}
                style={{
                  background: 'var(--light-bg)',
                  border: 'none',
                  borderRadius: '50%',
                  width: '32px',
                  height: '32px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'pointer'
                }}
              >
                <X size={18} />
              </button>
            </div>
            <div style={{ background: '#000', borderRadius: '12px', overflow: 'hidden', maxHeight: '70vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <img
                src={previewReceipt}
                alt="GCash Receipt"
                style={{ maxWidth: '100%', maxHeight: '70vh', objectFit: 'contain' }}
              />
            </div>
            <div style={{ marginTop: '16px', display: 'flex', justifyContent: 'flex-end' }}>
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => setPreviewReceipt(null)}
                style={{ padding: '8px 20px', borderRadius: '10px' }}
              >
                Close Receipt
              </button>
            </div>
          </div>
        </div>
      )}

      <style>{`
        .view-transition { animation: fadeIn 0.35s ease-out; }
        @keyframes fadeIn { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: translateY(0); } }
        .close-btn { background: var(--light-bg); border: none; width: 36px; height: 36px; border-radius: 50%; display: flex; align-items: center; justify-content: center; cursor: pointer; color: var(--text-main); transition: var(--transition); border: 1px solid var(--border); }
        .close-btn:hover { background: var(--surface); transform: rotate(90deg); }
        .icon-btn { background: var(--light-bg); border: 1px solid var(--border); width: 36px; height: 36px; border-radius: 12px; display: flex; align-items: center; justify-content: center; cursor: pointer; color: var(--text-main); transition: var(--transition); }
        .icon-btn:hover { background: var(--surface); transform: translateX(-4px); }
        .booking-card-hover:hover { transform: translateY(-4px); box-shadow: 0 12px 30px rgba(0,0,0,0.06); border-color: rgba(29, 211, 176, 0.4) !important; }
        .react-datepicker { border: none !important; font-family: inherit !important; box-shadow: 0 4px 12px rgba(0,0,0,0.05); }
        .react-datepicker__header { background: var(--light-bg) !important; border-bottom: none !important; }
        .react-datepicker__day--selected, .react-datepicker__day--in-selecting-range, .react-datepicker__day--in-range { background-color: var(--primary) !important; color: white !important; }
        .react-datepicker__day--disabled { color: #ccc !important; text-decoration: line-through; }
      `}</style>

      {viewerPhotos && (
        <ReviewPhotoViewer
          photos={viewerPhotos}
          initialIndex={viewerIndex}
          onClose={() => setViewerPhotos(null)}
        />
      )}
    </div>
  );
};

const BookingCard = ({ booking, onDelete, onUpdateStatus, hasConflict, onClick, onTouristClick }) => {
  const [photo, setPhoto] = useState(null);
  const [realName, setRealName] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [gcashName, setGcashName] = useState(null);
  const [gcashNumber, setGcashNumber] = useState(null);

  useEffect(() => {
    const fetchTouristData = async () => {
      if (!booking.touristUid) return;
      try {
        const userSnap = await get(ref(db, `users/${booking.touristUid}`));
        if (userSnap.exists()) {
          const val = userSnap.val();
          if (val.profilePicUrl) {
            setPhoto(val.profilePicUrl);
          }
          const tName = val.firstName || val.name || val.fullName;
          const tLast = val.lastName ? ` ${val.lastName}` : '';
          if (tName) setRealName(`${tName}${tLast}`.trim());
          setGcashName(val.gcashName && val.gcashName.trim() ? val.gcashName : 'N/A');
          setGcashNumber(val.gcashNumber && val.gcashNumber.trim() ? val.gcashNumber : 'N/A');
        }
      } catch (e) {
        console.error("Tourist data fetch error", e);
      }
    };
    fetchTouristData();
  }, [booking.touristUid]);

  let isOverdue = false;
  const bookingDateVal = booking.bookingDate || booking.checkInDate || booking.date;
  if (booking.status === 'Checked In' && bookingDateVal) {
    try {
      const parsed = parseDateSafely(bookingDateVal);
      if (parsed) {
        const endDate = addDays(parsed, parseInt(booking.nights) || 1);
        const todayMidnight = new Date();
        todayMidnight.setHours(0, 0, 0, 0);
        if (todayMidnight > endDate || todayMidnight.getTime() === endDate.getTime()) {
          isOverdue = true;
        }
      }
    } catch (e) {}
  }

  return (
    <div className="card booking-card-hover" onClick={onClick} style={{
      position: 'relative',
      marginBottom: '0',
      border: isOverdue ? '2px solid #EF4444' : (hasConflict ? '2px solid var(--primary)' : '1px solid rgba(0,0,0,0.05)'),
      padding: '20px',
      cursor: onClick ? 'pointer' : 'default',
      transition: 'var(--transition)'
    }}>

      <div style={{ display: 'flex', gap: '20px' }}>
        <div style={{ flex: 1 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '12px' }}>
            <div 
              style={{
                width: '44px', height: '44px', borderRadius: '14px',
                background: '#F3F4F6', overflow: 'hidden',
                display: 'flex', justifyContent: 'center', alignItems: 'center', color: 'var(--secondary)',
                cursor: 'pointer'
              }}
              onClick={(e) => { e.stopPropagation(); if(onTouristClick) onTouristClick(); }}
            >
              {photo ? (
                <img src={photo} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
              ) : (
                <User size={22} />
              )}
            </div>
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                <h4 
                  style={{ margin: 0, fontSize: '16px', fontWeight: 800, cursor: 'pointer' }}
                  onClick={(e) => { e.stopPropagation(); if(onTouristClick) onTouristClick(); }}
                  onMouseOver={(e) => e.target.style.textDecoration = 'underline'}
                  onMouseOut={(e) => e.target.style.textDecoration = 'none'}
                >
                  {realName || booking.touristName || 'Guest'}
                </h4>
                {(['Pending', 'Reschedule Requested', 'Refund Requested'].includes(booking.status) || isOverdue) && (
                  <span style={{ background: '#EF4444', color: 'white', fontSize: '10px', fontWeight: 800, padding: '2px 8px', borderRadius: '12px' }}>
                    Action Needed {isOverdue ? '(Overdue Checkout)' : ''}
                  </span>
                )}
              </div>
              <span className={`status-badge status-${(booking.status || 'pending').toLowerCase().replace(' ', '-')}`}>
                {booking.status || 'Pending'}
              </span>
              {booking.isHistorical && (
                <span style={{
                  background: 'rgba(29, 211, 176, 0.15)',
                  color: 'var(--secondary)',
                  fontSize: '11px',
                  fontWeight: 800,
                  padding: '3px 8px',
                  borderRadius: '12px',
                  border: '1px solid var(--secondary)',
                  marginLeft: '6px'
                }}>
                  Imported
                </span>
              )}
              {Boolean((booking.gcashReceipt && booking.gcashReceipt !== 'MANUAL_GCASH_PAYMENT') || booking.historicalPhotoUrl || booking.paymentReceiptDataUrl) && (
                <span
                  title="Has Attached Photo / Receipt"
                  style={{
                    background: 'rgba(59, 130, 246, 0.12)',
                    color: '#2563EB',
                    fontSize: '11px',
                    fontWeight: 700,
                    padding: '3px 8px',
                    borderRadius: '12px',
                    border: '1px solid rgba(59, 130, 246, 0.3)',
                    marginLeft: '6px',
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '4px'
                  }}
                >
                  <Camera size={12} /> Photo
                </span>
              )}
            </div>
          </div>

          <div style={{ background: 'var(--light-bg)', padding: '16px', borderRadius: '16px', marginBottom: '20px', border: '1px solid var(--border)' }}>
            <p style={{ margin: '0 0 4px 0', fontWeight: 800, fontSize: '15px' }}>{booking.activityTitle || booking.roomTitle}</p>
            <div style={{ fontSize: '13px', color: 'var(--text-muted)', display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
              {booking.isHistorical === true ? (
                <>
                  <span style={{ fontWeight: 700 }}>
                    Arrival: {(() => {
                      const pStart = parseDateSafely(booking.bookingDate || booking.checkInDate || booking.date);
                      return pStart ? format(pStart, 'MMM dd, yyyy') : (booking.bookingDate || 'N/A');
                    })()}
                  </span>
                  <span>•</span>
                  <span style={{ fontWeight: 700 }}>
                    Departure: {(() => {
                      const pStart = parseDateSafely(booking.bookingDate || booking.checkInDate || booking.date);
                      const duration = parseInt(booking.hours || booking.nights || 1);
                      const pDep = parseDateSafely(booking.departureDate || booking.checkOutDate) || (pStart ? addDays(pStart, duration) : null);
                      return pDep ? format(pDep, 'MMM dd, yyyy') : (booking.departureDate || 'N/A');
                    })()}
                  </span>
                </>
              ) : (
                <span style={{ fontWeight: 700 }}>
                  {formatBookingDateRange(booking)}
                </span>
              )}
              <span>•</span>
              {(() => {
                const isAct = booking.isActivityBooking === true || (
                  !booking.roomId &&
                  !booking.roomTitle &&
                  !(booking.activityTitle && String(booking.activityTitle).toLowerCase().includes('room')) &&
                  !booking.nights &&
                  Boolean(booking.activityId && String(booking.activityId).trim() !== '')
                );
                const duration = parseInt(booking.hours || booking.nights || 1);
                return <span>{duration} {isAct ? 'Hour/s' : (duration === 1 ? 'Night' : 'Nights')}</span>;
              })()}
              <span>•</span>
              <span style={{ fontWeight: 800, color: 'var(--secondary)' }}>₱{booking.totalPrice}</span>
            </div>
            {(booking.arrivalTime || booking.timeSlot || booking.bookingTime) && (
              <div style={{ marginTop: '6px', fontSize: '12.5px', color: 'var(--text-main)', display: 'flex', alignItems: 'center', gap: '6px', fontWeight: 600 }}>
                <Clock size={14} color="var(--primary)" />
                <span>Arrival: <strong style={{ color: 'var(--primary)' }}>{booking.arrivalTime || booking.timeSlot || booking.bookingTime}</strong></span>
              </div>
            )}
            {booking.paymentOption && (
              <div style={{ marginTop: '8px', fontSize: '12px', fontWeight: 700, color: 'var(--primary)', display: 'flex', alignItems: 'center', gap: '4px' }}>
                <CreditCard size={14} /> {booking.paymentOption}
              </div>
            )}

            {(booking.ocrStatus || booking.extractedRefNo) && (() => {
              const ocrStatus = booking.ocrStatus || 'Unverified';
              const isVerified = ocrStatus === 'Verified';
              const rawReason = booking.ocrIssues || booking.ocrReason || booking.flaggedReason || booking.verificationDetails;
              let reasonText = '';
              if (typeof rawReason === 'string') {
                reasonText = rawReason.trim();
              } else if (Array.isArray(rawReason)) {
                reasonText = rawReason.filter(Boolean).join(', ').trim();
              } else if (rawReason && typeof rawReason === 'object') {
                reasonText = Object.values(rawReason).filter(Boolean).join(', ').trim();
              }
              if (!reasonText && !isVerified) {
                reasonText = 'No detailed reason was recorded for this booking.';
              }

              return (
                <div style={{ marginTop: '8px', padding: '10px', background: isVerified ? 'rgba(16, 185, 129, 0.1)' : 'rgba(245, 158, 11, 0.1)', border: `1px solid ${isVerified ? 'rgba(16, 185, 129, 0.2)' : 'rgba(245, 158, 11, 0.2)'}`, borderRadius: '10px', display: 'flex', alignItems: 'flex-start', gap: '8px' }}>
                  {isVerified ? <CheckCircle2 size={14} color="#059669" style={{marginTop: '2px'}}/> : <AlertCircle size={14} color="#B45309" style={{marginTop: '2px'}}/>}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <span style={{ fontSize: '12px', fontWeight: 800, color: isVerified ? '#059669' : '#B45309', display: 'block' }}>
                      AI Verification: {isVerified ? 'Passed' : (ocrStatus === 'Multiple Receipts (Manual Review)' ? 'Multiple Receipts' : 'Flagged for Review')}
                    </span>
                    {booking.extractedRefNo && (
                      <span style={{ fontSize: '11px', color: isVerified ? '#059669' : '#B45309', display: 'block', marginTop: '2px', fontWeight: 600 }}>
                        Ref No: {booking.extractedRefNo}
                      </span>
                    )}
                    {reasonText && (
                      <span style={{ fontSize: '11px', color: '#B45309', display: 'block', marginTop: '4px', fontWeight: 600, wordBreak: 'break-word' }}>
                        Reason: {reasonText}
                      </span>
                    )}
                  </div>
                </div>
              );
            })()}
            {booking.status === 'Reschedule Requested' && (
              <div style={{ marginTop: '8px', fontSize: '12px', fontWeight: 700, color: '#818CF8', background: 'rgba(79, 70, 229, 0.1)', padding: '8px', borderRadius: '8px' }}>
                <div style={{ marginBottom: '4px' }}>Reschedule to: {booking.requestedRescheduleDate} ({booking.requestedRescheduleNights || booking.nights} Night/s)</div>
                <div>Reason: {booking.rescheduleReason || 'None provided'}</div>
              </div>
            )}
            {booking.status === 'Refund Requested' && (
              <div style={{ marginTop: '8px', fontSize: '12px', fontWeight: 700, color: '#B45309', background: 'rgba(245, 158, 11, 0.15)', border: '1px solid rgba(245, 158, 11, 0.3)', padding: '10px', borderRadius: '10px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '6px', color: '#B45309', fontWeight: 800 }}>
                  <AlertCircle size={15} /> REFUND REQUEST IN PROCESS (Please review and settle)
                </div>
                <div style={{ marginBottom: '4px', color: 'var(--text-main)' }}>Refund Reason: <strong>{booking.refundReason || 'No reason provided'}</strong></div>
                <div style={{ color: 'var(--text-main)' }}>Send Refund To: <strong>{booking.touristGcashName || gcashName || booking.gcashName || 'N/A'} ({booking.touristGcashNumber || gcashNumber || booking.gcashNumber || 'N/A'})</strong></div>
              </div>
            )}

            {booking.cancellationReason && (
              <div style={{ marginTop: '8px', fontSize: '12px', fontWeight: 700, color: '#DC2626', background: '#FEE2E2', padding: '8px', borderRadius: '8px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                <AlertCircle size={14} /> Owner Note/Reason: {booking.cancellationReason}
              </div>
            )}

            <div style={{ marginTop: '16px', display: 'flex', justifyContent: 'center' }}>
              <span style={{ background: 'rgba(79, 70, 229, 0.1)', color: '#818CF8', padding: '8px 16px', borderRadius: '12px', fontSize: '12px', fontWeight: '800', display: 'flex', alignItems: 'center', gap: '6px', border: '1px solid rgba(79, 70, 229, 0.2)', transition: 'var(--transition)' }}>
                <Eye size={14} /> Tap to view full details & actions
              </span>
            </div>
          </div>

          {hasConflict && (
            <div style={{ color: 'var(--primary)', fontSize: '12px', display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '16px', background: 'rgba(251, 54, 64, 0.15)', padding: '10px', borderRadius: '10px', fontWeight: 600 }}>
              <AlertCircle size={16} /> Overlaps with an existing reservation.
            </div>
          )}

        </div>
      </div>
    </div>
  );
};

export default OwnerDashboard;
